import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Select, Store } from '@ngxs/store';
import moment from 'moment';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription, catchError, forkJoin, map, of, tap } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { ComanageService } from 'src/app/comanage/comanage.service';
import DefaultProjectEmployeeLinkDTO from 'src/app/models/DefaultProjectEmployeeLinkDTO';
import { Employee } from 'src/app/models/Employee';
import { JiraProjectCredential } from 'src/app/models/JiraProjectCredential';
import { LinkedFileToObject } from 'src/app/models/LinkedFileToObject';
import Project from 'src/app/models/Project';
import { Timesheet } from 'src/app/models/Timesheet';
import { Timeslot } from 'src/app/models/Timeslot';
import { UploadedDocument } from 'src/app/models/UploadedDocument';
import { AddDefaultEmployees, DeleteDefaultEmployee, FetchProjectById } from 'src/app/projects/project.actions';
import { ProjectService } from 'src/app/projects/project.service';
import { ProjectState } from 'src/app/projects/project.state';
import { AddFileToObjectModalComponent } from 'src/app/shared/global-files/add-file-to-object-modal/add-file-to-object-modal.component';
import { GlobalFilesService } from 'src/app/shared/global-files/global-files.service';
import { JiraLoginService } from 'src/app/shared/login-components/jira-login/jira-login.service';
import { environment } from 'src/environments/environment';

import { TimesheetAiValidatorService } from '../timesheet-ai-validator.service';
import { SetTimeslotToIsBilled } from '../timesheets.actions';
import { TimesheetsService } from '../timesheets.service';
import { openExternalUrl } from '../../shared/safe-url';

@Component({
    selector: 'app-invoice-dialog',
    templateUrl: './invoice-dialog.component.html',
    styleUrls: ['./invoice-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class InvoiceDialogComponent implements OnInit {
    private subscriptions = new Subscription();
    readonly addNewSesDocumentValue = '__ADD_NEW_SES_DOCUMENT__';
    readonly addNewPoDocumentValue = '__ADD_NEW_PO_DOCUMENT__';
    selectedTimeslots: Timeslot[] = [];
    timeslots: Timeslot[];
    timesheets: Timesheet[];
    uniqueProjects: Project[];
    employee: Employee;
    project: Project;
    isLoaded = false;
    // Count outstanding background requests to show a global loader overlay
    private pendingRequests = 0;
    isDisabled = true;
    partnerName: string;
    invoiceDate: string;
    subjectWithDate: string;
    addProjectManagementFee = false;
    comanageApiKey: string | null = null;
    comanageSuppliers: any[] = [];
    supplierSearchText: string = '';
    selectedComanageSupplierId: number | null = null;
    private savingComanageSupplier = false;
    selectedAttachments: Array<{ timeslotUuid: string; pdfUrl: string; fileName: string }> = [];
    // AI validation results per selected attachment
    attachmentValidationResults: Record<
        string,
        {
            ok: boolean;
            issues: string[];
            type?: string;
            details?: {
                parsedHours?: number | null;
                pdfMonthISO?: string | null;
                expectedMonthISO?: string | null;
                hoursOk?: boolean;
                monthOk?: boolean;
                signed?: boolean;
                employeePresent?: boolean;
            };
        }
    > = {};
    isValidatingAttachment: Record<string, boolean> = {};
    poNumber: string = '';
    sesNumber: string = '';
    poDocuments: Array<{ value: string; number: string; label: string; doc: LinkedFileToObject }> = [];
    sesDocuments: Array<{ value: string; number: string; label: string; doc: LinkedFileToObject; matchesPeriod: boolean }> = [];
    selectedPoDoc: LinkedFileToObject | null = null;
    selectedSesDoc: LinkedFileToObject | null = null;
    selectedPoFileId: string = '';
    selectedSesFileId: string = '';
    comanageInvoiceText: string = '';
    jiraReferenceKey: string = '';
    jiraReferenceName: string = '';
    jiraReferenceDisplay: string = '';
    jiraRawKey: string = '';
    jiraProjectKeyList$: JiraProjectCredential[] = [];
    jiraProjectKeysLoaded = false;
    tempoAccounts: Array<{ id: string; key?: string; name?: string; status?: string }> = [];
    projectAccounts: Array<any> = [];
    selectedTempoAccountIds: string[] = [];
    // Finalized invoice metadata (set after successful finalize)
    finalizedInvoice: any = null;
    finalizedInvoicePdfUrl: string | null = null;
    finalizedInvoiceLink: string | null = null;
    finalizedInvoiceNumber: string | number | null = null;
    finalizedInvoiceId: string | null = null;
    finalizedInvoiceStatus: string | null = null;
    finalizedInvoiceDate: string | null = null;
    finalizedInvoiceResults: any[] = [];
    comanageInvoices: any[] = [];
    filteredComanageInvoices: any[] = [];
    existingInvoiceSearch = '';
    selectedExistingInvoiceNumber = '';
    // Per-account worklog totals (current invoice month), formatted like "1h 15m"
    accountWorklogDurationLabelById: Record<string, string> = {};
    // Raw billable seconds per account id (for auto-selection)
    accountWorklogSecondsById: Record<string, number> = {};
    // Worklog date range (editable): defaults to first/last day of current month
    worklogsFrom: string = '';
    worklogsTo: string = '';
    // Messages shown when no project account price found for selected account+period
    missingAccountPriceMessages: string[] = [];
    // Account-mode aggregated ticket lines
    accountIssueLines: Array<{
        accountId: string;
        accountKey?: string;
        accountLabel?: string;
        issueId: string;
        jiraKey?: string;
        summary?: string;
        totalHours: number;
        effectiveRate: number; // derived average €/h across contributors
        initialEffectiveRate?: number;
        totalAmount: number;
        selected: boolean;
    }> = [];
    private rateChangeNoticeShownByAccountId: Set<string> = new Set<string>();
    invoiceMode: 'time' | 'account' | 'ses' = 'time';
    previousTimesheetGroups: Array<{ label: string; timeslots: Timeslot[] }> = [];
    previousTimesheets: Timesheet[] = [];
    isLoadingPreviousTimeslots = false;
    showPreviousTimeslots = false;
    additionalMonthsBefore = 0;
    additionalMonthsAfter = 0;

    projectManagementPercentage: number = 15;

    // --- Project Management (PM) and Total Calculation Methods ---
    public calculatePMCost(accountLines: any[], pmPercentage: number): number {
        if (!Array.isArray(accountLines) || !pmPercentage) return 0;
        const subtotal = accountLines.filter((l) => l.selected).reduce((sum, l) => sum + (l.totalAmount || 0), 0);
        return +(subtotal * (pmPercentage / 100)).toFixed(2);
    }

    public calculateAccountTotalWithPM(): number {
        // Use selected account lines
        const subtotal = (this.accountIssueLines || []).filter((l) => l.selected).reduce((sum, l) => sum + (l.totalAmount || 0), 0);
        let total = subtotal;
        if (this.addProjectManagementFee) {
            const pmCost = subtotal * (this.projectManagementPercentage / 100);
            total += pmCost;
        }
        return +total.toFixed(2);
    }

    public calculateAccountSubtotal(accountLines: any[]): number {
        if (!Array.isArray(accountLines)) return 0;
        return +accountLines
            .filter((l) => l.selected)
            .reduce((sum, l) => sum + (l.totalAmount || 0), 0)
            .toFixed(2);
    }

    constructor(
        private store: Store,
        private projectService: ProjectService,
        private timesheetsService: TimesheetsService,
        private comanageService: ComanageService,
        private logger: NGXLogger,
        private toastr: ToastrService,
        private jiraLoginService: JiraLoginService,
        private tsAiValidator: TimesheetAiValidatorService,
        private globalFilesService: GlobalFilesService,
        private cdr: ChangeDetectorRef,
        private modalService: NgbModal,
        public dialogRef: MatDialogRef<InvoiceDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
    ) {
        this.timeslots = data.projectTimeslots;
        this.timesheets = data.timesheets;
        this.uniqueProjects = data.uniqueProjects;
    }

    private track<T>(obs: Observable<T>): Observable<T> {
        try {
            this.pendingRequests++;
        } catch {}
        return obs.pipe(
            finalize(() => {
                try {
                    this.pendingRequests = Math.max(0, this.pendingRequests - 1);
                    // Trigger change detection so overlay updates immediately
                    try {
                        this.cdr.detectChanges();
                    } catch {}
                } catch {}
            }),
        );
    }

    isLoadingAll(): boolean {
        return (this.pendingRequests || 0) > 0;
    }

    ngOnInit(): void {
        this.logger.debug('InvoiceDialog: ngOnInit started');

        // First load full project details with linkedEmployees
        this.subscriptions.add(
            this.track(this.loadFullProjectDetails()).subscribe(() => {
                this.subscriptions.add(this.track(this.getProjectDetails()).subscribe());
                this.invoiceDate = moment(this.createInvoiceDate()).format('YYYY-MM-DD');
                this.subjectWithDate = this.getSubjectWithDateByTimesheet();
                // Initialize worklog date range to current month
                const initial = this.computeMonthRange();
                this.worklogsFrom = initial.from;
                this.worklogsTo = initial.to;
                this.loadPreviousUnbilledTimeslots();
                // Preload saved validation for all available PDF attachments
                this.preloadExistingValidationForAllPdfs();
            }),
        );

        // Load Jira projects (reuse existing service) to map ID -> name
        this.subscriptions.add(
            this.track(this.jiraLoginService.getJiraProjectKeys()).subscribe((data: Object[]) => {
                this.jiraProjectKeyList$ = [];
                for (const obj of data) {
                    const name = obj['projectName'] ? obj['projectName'] : '';
                    const key = obj['projectKey'] ? obj['projectKey'] : '';
                    const id = obj['projectId'] ? obj['projectId'] : '';
                    const cred = new JiraProjectCredential(name, key, id);
                    this.jiraProjectKeyList$.push(cred);
                }
                this.jiraProjectKeysLoaded = true;
                this.updateJiraReferenceName();
                // Prefer deriving textual Jira key from known projectId mapping
                this.updateJiraRawKeyFromList();
                // Also derive numeric Jira project id from textual key when missing
                this.updateJiraReferenceIdFromList();
                this.jiraReferenceDisplay = this.computeReferenceDisplay();
                // Try fetching Tempo accounts for this project
                this.fetchTempoAccountsForCurrentProject();
            }),
        );

        // Load Comanage suppliers for selection
        this.subscriptions.add(
            this.track(this.comanageService.getContacts()).subscribe(
                (contacts: any[]) => {
                    this.comanageSuppliers = (contacts || []).sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''));
                    // If supplier was already loaded from logbook, prefill name now
                    if (this.selectedComanageSupplierId) {
                        const storedSupplier = this.comanageSuppliers.find((s) => s.number === this.selectedComanageSupplierId);
                        if (storedSupplier) {
                            this.supplierSearchText = storedSupplier.name;
                        }
                    } else {
                        // Fallback snapshot read from logbook state (if already present)
                        const storedId = this.getStoredComanageSupplierId();
                        if (storedId) {
                            this.selectedComanageSupplierId = storedId;
                            const storedSupplier = this.comanageSuppliers.find((s) => s.number === storedId);
                            if (storedSupplier) {
                                this.supplierSearchText = storedSupplier.name;
                            }
                        }
                    }
                },
                () => {
                    this.comanageSuppliers = [];
                },
            ),
        );

        // Get Comanage API key
        this.logger.debug('InvoiceDialog: Fetching Comanage API key...');
        this.subscriptions.add(
            this.track(this.comanageService.getComanageApiKey()).subscribe(
                (apiKey) => {
                    this.comanageApiKey = apiKey;
                    this.logger.debug('InvoiceDialog: Comanage API key retrieved:', apiKey ? '***KEY_PRESENT***' : 'NULL');
                    if (apiKey) {
                        this.loadComanageInvoices();
                    }
                },
                (error) => {
                    this.logger.error('InvoiceDialog: Failed to retrieve Comanage API key:', error);
                },
            ),
        );
    }

    private loadComanageInvoices(): void {
        this.subscriptions.add(
            this.track(this.comanageService.getInvoices()).subscribe(
                (response: any) => {
                    const parsed = this.parseComanageInvoicesPayload(response)
                        .filter((invoice: any) => !!this.getComanageInvoiceNumber(invoice))
                        .sort((left: any, right: any) => {
                            const leftDate = new Date(left?.date || left?.created_at || 0).getTime();
                            const rightDate = new Date(right?.date || right?.created_at || 0).getTime();
                            return rightDate - leftDate;
                        });

                    this.comanageInvoices = parsed;
                    this.applyExistingInvoiceFilter();
                },
                (error) => {
                    this.logger.warn('InvoiceDialog: Failed to load existing Comanage invoices', error);
                    this.comanageInvoices = [];
                    this.filteredComanageInvoices = [];
                },
            ),
        );
    }

    private parseComanageInvoicesPayload(payload: any): any[] {
        if (Array.isArray(payload)) {
            return payload;
        }

        if (typeof payload === 'string') {
            try {
                const parsed = JSON.parse(payload);
                return Array.isArray(parsed) ? parsed : [];
            } catch {
                return [];
            }
        }

        if (Array.isArray(payload?.data)) {
            return payload.data;
        }

        return [];
    }

    private normalizeInvoiceNumber(value: any): string {
        if (value === null || value === undefined) {
            return '';
        }
        return String(value).trim();
    }

    getComanageInvoiceNumber(invoice: any): string {
        return this.normalizeInvoiceNumber(invoice?.invoice_number ?? invoice?.number ?? invoice?.invoiceNumber ?? invoice?.id);
    }

    getComanageInvoiceId(invoice: any): string {
        return this.normalizeInvoiceNumber(invoice?.id ?? invoice?.uuid ?? invoice?.invoice_id ?? invoice?.invoiceId);
    }

    getComanageInvoiceStatus(invoice: any): string {
        return this.normalizeInvoiceNumber(invoice?.status ?? invoice?.state);
    }

    getComanageInvoiceDate(invoice: any): string {
        return this.normalizeInvoiceNumber(invoice?.date ?? invoice?.created_at ?? invoice?.invoice_date);
    }

    private buildComanageEditLink(invoice: any): string | null {
        if (!invoice) {
            return null;
        }

        const candidates = [invoice?.link, invoice?.url, invoice?.html_url, invoice?.edit_url, invoice?.pdf, invoice?.pdf_url, invoice?.download_pdf_url]
            .filter((value) => !!value)
            .map((value) => String(value).trim());

        for (const candidate of candidates) {
            const match = candidate.match(/(invoices|creditnotes)\/(\d+)(?:\/[^?#]*)?/i);
            if (!match) {
                continue;
            }

            const entity = match[1].toLowerCase();
            const number = match[2];

            const candidateOriginMatch = candidate.match(/https?:\/\/[^\/]+/i);
            const candidateOrigin = candidateOriginMatch ? candidateOriginMatch[0] : '';
            const isApiHost = candidateOrigin.includes('api.comanage.me');
            const appOrigin = !isApiHost && candidateOrigin.length > 0 ? candidateOrigin : null;
            const origin = appOrigin || this.getComanageAppOrigin();

            return `${origin}/${entity}/${number}/edit`;
        }

        const invoiceId = this.getComanageInvoiceId(invoice);
        if (!invoiceId) {
            return null;
        }

        return `${this.getComanageAppOrigin()}/invoices/${invoiceId}/edit`;
    }

    private getComanageAppOrigin(): string {
        const browserOrigin = typeof window !== 'undefined' ? window.location.origin : '';
        if (browserOrigin.includes('.comanage.me')) {
            return browserOrigin;
        }
        return 'https://app.comanage.me';
    }

    getComanageInvoiceDisplay(invoice: any): string {
        const invoiceNumber = this.getComanageInvoiceNumber(invoice);
        const title = String(invoice?.title || invoice?.subject || '').trim();
        const date = String(invoice?.date || invoice?.created_at || '').trim();
        const status = String(invoice?.status || invoice?.state || '').trim();

        const right = [title, date, status].filter((value) => value.length > 0).join(' · ');
        return right.length > 0 ? `${invoiceNumber} — ${right}` : invoiceNumber;
    }

    applyExistingInvoiceFilter(): void {
        const filter = (this.existingInvoiceSearch || '').trim().toLowerCase();
        if (!filter) {
            this.filteredComanageInvoices = [...this.comanageInvoices].slice(0, 200);
            return;
        }

        this.filteredComanageInvoices = this.comanageInvoices
            .filter((invoice) => {
                const haystack = [
                    this.getComanageInvoiceNumber(invoice),
                    String(invoice?.title || ''),
                    String(invoice?.subject || ''),
                    String(invoice?.status || invoice?.state || ''),
                    String(invoice?.date || ''),
                ]
                    .join(' ')
                    .toLowerCase();

                return haystack.includes(filter);
            })
            .slice(0, 200);
    }

    private getSelectedExistingInvoice(): any | null {
        const selected = (this.selectedExistingInvoiceNumber || '').trim();
        if (!selected) {
            return null;
        }

        return this.comanageInvoices.find((invoice) => this.getComanageInvoiceNumber(invoice) === selected) || null;
    }

    linkExistingInvoiceToSelectedTimeslots(): void {
        const selectedInvoice = this.getSelectedExistingInvoice();
        if (!selectedInvoice) {
            this.toastr.error('Kies eerst een bestaande factuur.');
            return;
        }

        if ((this.selectedTimeslots || []).length === 0) {
            this.toastr.error('Selecteer minstens één timeslot om te linken.');
            return;
        }

        this.finalizedInvoice = selectedInvoice;
        this.finalizedInvoiceNumber = this.getComanageInvoiceNumber(selectedInvoice);
        this.finalizedInvoiceId = this.getComanageInvoiceId(selectedInvoice) || null;
        this.finalizedInvoiceStatus = this.getComanageInvoiceStatus(selectedInvoice) || null;
        this.finalizedInvoiceDate = this.getComanageInvoiceDate(selectedInvoice) || null;
        this.finalizedInvoicePdfUrl = selectedInvoice?.pdf || selectedInvoice?.pdf_url || selectedInvoice?.download_pdf_url || null;
        this.finalizedInvoiceLink = this.buildComanageEditLink(selectedInvoice);

        this.persistInvoiceReferencesForSelectedTimeslots();
        this.toastr.success('Bestaande factuur gelinkt aan geselecteerde timeslots.');
        this.closeDialog();
    }

    onJiraKeySelected(event: any): void {
        try {
            const selectedText = (event?.target?.value || '').toString().trim();
            if (!selectedText) return;
            // Accept either key or label containing name - key
            let selectedKey = selectedText;
            const byExactKey = this.jiraProjectKeyList$.find((c) => (c?.jiraKey || '') === selectedText);
            if (!byExactKey) {
                const match = this.jiraProjectKeyList$.find((c) => `${c.projectName} - ${c.jiraKey}` === selectedText);
                if (match) {
                    selectedKey = match.jiraKey;
                }
            }
            // Update local Jira key mappings
            this.jiraRawKey = selectedKey;
            this.updateJiraReferenceIdFromList();
            this.updateJiraReferenceName();
            this.jiraReferenceDisplay = this.computeReferenceDisplay();
            // Do not persist to project for now; just refresh dependent data
            this.fetchTempoAccountsForCurrentProject();
        } catch (e) {
            this.logger.warn('onJiraKeySelected failed', e);
        }
    }

    setInvoiceMode(mode: 'time' | 'account' | 'ses'): void {
        this.invoiceMode = mode;
        // Default: project management fee off in 'By Time' mode
        if (this.invoiceMode === 'time') {
            this.addProjectManagementFee = false;
        } else if (this.invoiceMode === 'account') {
            // Default: project management fee ON in 'By Account' mode
            this.addProjectManagementFee = true;
        } else if (this.invoiceMode === 'ses') {
            this.addProjectManagementFee = false;
            this.applySelectedSesToTimeslots();
        }
        // Recompute reference display to include account key suffix in 'account' mode
        this.jiraReferenceDisplay = this.computeReferenceDisplay();
        this.refreshInvoiceFieldsByMode();
        this.updateCreateDisabled();
    }

    private loadFullProjectDetails(): Observable<any> {
        // Simply return - we'll get linkedEmployees from store on demand in getPricePerHourForTimeslot
        return of(null);
    }

    getProjectDetails(): Observable<Project> {
        this.project = null;

        return this.projectService.getProjectDetail(this.timeslots[0].project.uuid).pipe(
            tap((project: Project) => {
                this.project = project;
                const storedSupplierId = this.parseSupplierId(project?.comanageContactId);
                if (storedSupplierId) {
                    this.selectedComanageSupplierId = storedSupplierId;
                    const storedSupplier = this.comanageSuppliers.find((supplier) => Number(supplier?.number) === storedSupplierId);
                    if (storedSupplier) {
                        this.supplierSearchText = storedSupplier.name;
                    }
                }
                // Resolve Jira reference (id/key) from project, timeslots or uniqueProjects
                this.jiraReferenceKey = this.resolveJiraReference();
                this.jiraRawKey = this.resolveJiraRawKey();
                // Map textual key -> numeric id and vice versa using loaded project list
                this.updateJiraReferenceIdFromList();
                this.updateJiraRawKeyFromList();
                // Update derived name and display after mapping to ensure correctness
                this.updateJiraReferenceName();
                this.jiraReferenceDisplay = this.computeReferenceDisplay();
                this.partnerName = this.getPartnerName();
                this.subjectWithDate = this.getSubjectWithDateByTimesheet();
                // Set default project management fee based on project type
                this.setDefaultProjectManagementFee();
                // Set default invoice mode from project.invoiceMethod. Fallback:
                // CONSULTANCY -> by time, all other project types -> by account.
                try {
                    const method = (this.project?.invoiceMethod || '').toString().toUpperCase();
                    const t = (this.project?.projectType || '').toString().toUpperCase();
                    if (method === 'BY_SES') {
                        this.invoiceMode = 'ses';
                        this.addProjectManagementFee = false;
                    } else if (method === 'BY_ACCOUNT') {
                        this.invoiceMode = 'account';
                        this.addProjectManagementFee = true;
                    } else if (method === 'BY_TIME' || t.includes('CONSULT')) {
                        this.invoiceMode = 'time';
                        this.addProjectManagementFee = false;
                    } else {
                        this.invoiceMode = 'account';
                        this.addProjectManagementFee = true;
                    }
                } catch {
                    /* noop */
                }
                this.isLoaded = true;
                // Load default PO/SES/contract text from employee + project contract fields
                this.loadInvoiceFieldsFromContracts();
                // Load PO/SES document options from project linked files
                this.loadProjectDocuments();
                // Refresh Tempo accounts after project id/key is fully resolved
                this.fetchTempoAccountsForCurrentProject();
                // Prefill rates from project employees mapping once project is loaded
                (this.timeslots || []).forEach((slot) => {
                    if (!slot?.billed) {
                        const anySlot: any = slot as any;
                        const autoRate = this.getPricePerHourForTimeslot(slot);
                        if (autoRate && autoRate > 0) {
                            anySlot._editedRate = autoRate;
                        }
                    }
                });
                // For multi-project invoices, also load full details for the other projects
                // so that getPricePerHourForTimeslot returns the correct per-project rate.
                const otherProjectUuids = [...new Set((this.timeslots || []).map((t) => t.project?.uuid).filter(Boolean))].filter((id) => id !== project.uuid);
                if (otherProjectUuids.length > 0) {
                    this.subscriptions.add(
                        forkJoin(otherProjectUuids.map((id) => this.projectService.getProjectDetail(id).pipe(catchError(() => of(null))))).subscribe(
                            (otherProjects: Project[]) => {
                                (otherProjects || []).filter(Boolean).forEach((p: Project) => {
                                    const idx = this.uniqueProjects.findIndex((up) => up.uuid === p.uuid);
                                    if (idx >= 0) this.uniqueProjects[idx] = p;
                                    // Backfill any timeslots of this project that had no rate yet
                                    (this.timeslots || [])
                                        .filter((slot) => !slot.billed && slot.project?.uuid === p.uuid)
                                        .forEach((slot) => {
                                            if (!(slot as any)._editedRate) {
                                                const rate = this.getPricePerHourForTimeslot(slot);
                                                if (rate > 0) (slot as any)._editedRate = rate;
                                            }
                                        });
                                });
                            },
                        ),
                    );
                }
                // Recompute disabled state considering timesheet requirements
                this.updateCreateDisabled();
                // Load project accounts for this project (used for account-mode rates)
                if (this.project?.uuid) {
                    this.projectService.getProjectAccounts(this.project.uuid).subscribe(
                        (accounts: any) => {
                            this.projectAccounts = Array.isArray(accounts) ? accounts : accounts && (accounts.data || []);
                            this.refreshInvoiceFieldsByMode();
                        },
                        (err) => {
                            this.logger.warn('Failed to load project accounts', err);
                            this.projectAccounts = [];
                            this.refreshInvoiceFieldsByMode();
                        },
                    );
                }
            }),
        );
    }

    getInvoiceDialogTitle(): string {
        const projects = this.getInvoiceProjects();
        if (projects.length > 1) {
            return `${projects.length} projects`;
        }
        return this.project?.title || projects[0]?.title || this.jiraReferenceName || this.jiraReferenceDisplay || 'project';
    }

    getInvoiceProjects(): Project[] {
        const byId = new Map<string, Project>();
        (this.timeslots || []).forEach((slot) => {
            if (slot?.project?.uuid && !byId.has(slot.project.uuid)) {
                byId.set(slot.project.uuid, slot.project);
            }
        });
        return Array.from(byId.values());
    }

    private getInvoiceProjectIds(): string[] {
        return this.getInvoiceProjects()
            .map((project) => project?.uuid)
            .filter((uuid): uuid is string => !!uuid);
    }

    private getProjectTitleById(projectId: string | undefined): string {
        if (!projectId) return '';
        return this.getInvoiceProjects().find((project) => project.uuid === projectId)?.title || '';
    }

    private loadProjectDocuments(): void {
        const projectIds = this.getInvoiceProjectIds();
        if (projectIds.length === 0) {
            this.poDocuments = [];
            this.sesDocuments = [];
            return;
        }
        const invoicePeriod = this.getInvoiceDocumentRange();
        forkJoin(
            projectIds.map((projectId) =>
                this.globalFilesService.getAllFilesLinkedToObject(projectId).pipe(
                    map((result) => ({
                        projectId,
                        linkedFiles: Array.isArray(result?.linkedFiles) ? result.linkedFiles : [],
                    })),
                    catchError((error) => {
                        this.logger.warn('Failed to load project documents for project', projectId, error);
                        return of({ projectId, linkedFiles: [] as LinkedFileToObject[] });
                    }),
                ),
            ),
        ).subscribe(
            (results) => {
                const linkedFiles: Array<LinkedFileToObject & { _projectId?: string }> = [];
                results.forEach((result) => {
                    (result.linkedFiles || []).forEach((file) => linkedFiles.push({ ...file, _projectId: result.projectId }));
                });
                const uploadedDocs = linkedFiles.filter((f) => (f?.fileType || '').toLowerCase() === 'uploadeddocument');

                this.poDocuments = uploadedDocs
                    .filter((f) => (f.documentType || '').toUpperCase() === 'PO')
                    .filter((f) => this.documentOverlapsRange(f, invoicePeriod.from, invoicePeriod.to))
                    .map((f) => {
                        const poNum = (f.extractedPoNumber || '').trim();
                        const displayName = this.getDocumentDisplayName(f);
                        const periodLabel = this.getDocumentPeriodLabel(f);
                        const projectTitle = this.getProjectTitleById((f as any)._projectId);
                        const label = `${poNum ? `${poNum} — ` : ''}${displayName}${periodLabel ? ` (${periodLabel})` : ''}${
                            projectIds.length > 1 && projectTitle ? ` — ${projectTitle}` : ''
                        }`;
                        return { value: f.fileId, number: poNum, label, doc: f };
                    });

                this.sesDocuments = uploadedDocs
                    .filter((f) => (f.documentType || '').toUpperCase() === 'SES')
                    .map((f) => {
                        const sesRef = this.getSesDocumentReference(f);
                        const matchesPeriod = this.documentOverlapsRange(f, invoicePeriod.from, invoicePeriod.to);
                        const projectTitle = this.getProjectTitleById((f as any)._projectId);
                        const label = `${this.buildSesDocumentOptionLabel(f, matchesPeriod)}${
                            projectIds.length > 1 && projectTitle ? ` — ${projectTitle}` : ''
                        }`;
                        return { value: f.fileId, number: sesRef, label, doc: f, matchesPeriod };
                    })
                    .sort((a, b) => Number(b.matchesPeriod) - Number(a.matchesPeriod));

                if (this.selectedPoFileId && !this.poDocuments.some((doc) => doc.value === this.selectedPoFileId)) {
                    this.selectedPoFileId = '';
                    this.poNumber = '';
                    this.selectedPoDoc = null;
                }
                if (this.selectedSesFileId && !this.sesDocuments.some((doc) => doc.value === this.selectedSesFileId)) {
                    this.selectedSesFileId = '';
                    this.sesNumber = '';
                    this.selectedSesDoc = null;
                }

                // If only one option and field is empty, auto-select
                if (this.poDocuments.length === 1 && !(this.poNumber || '').trim()) {
                    this.selectedPoFileId = this.poDocuments[0].value;
                    this.poNumber = this.poDocuments[0].number;
                    this.selectedPoDoc = this.poDocuments[0].doc;
                }
                const bestSesDocument = this.getBestSesDocumentOption();
                if (bestSesDocument && (!(this.sesNumber || '').trim() || this.invoiceMode === 'ses')) {
                    this.selectedSesFileId = bestSesDocument.value;
                    this.sesNumber = bestSesDocument.number;
                    this.selectedSesDoc = bestSesDocument.doc;
                    // SES usually has a PO number reference — auto-fill PO if available
                    this.applySelectedSesData(bestSesDocument.doc);
                    this.enrichSelectedSesDocument();
                }
                if (this.invoiceMode === 'ses') {
                    this.applySelectedSesToTimeslots();
                    this.loadTimesheetsForSelectedSesPeriod();
                }
                this.updateCreateDisabled();
            },
            (err) => {
                this.logger.warn('Failed to load project documents for PO/SES', err);
                this.poDocuments = [];
                this.sesDocuments = [];
            },
        );
    }

    onPoDocumentSelected(fileId: string): void {
        if (fileId === this.addNewPoDocumentValue) {
            this.selectedPoFileId = this.selectedPoDoc?.fileId || '';
            this.openAddPoDocumentModal();
            this.updateCreateDisabled();
            return;
        }

        const match = this.poDocuments.find((d) => d.value === fileId);
        this.poNumber = match?.number || '';
        this.selectedPoDoc = match?.doc || null;
        this.updateCreateDisabled();
    }

    public openAddPoDocumentModal(): void {
        const project = this.project?.uuid ? this.project : this.getInvoiceProjects()[0];
        if (!project?.uuid) {
            this.toastr.error('Geen project gevonden om het PO-document aan te linken.');
            return;
        }

        const knownPoFileIds = new Set((this.poDocuments || []).map((doc) => doc.value).filter((value) => !!value));
        const modalRef = this.modalService.open(AddFileToObjectModalComponent, {
            windowClass: 'modal-prompt modal-above-mat-dialog',
            backdropClass: 'modal-above-mat-dialog-backdrop',
            animation: false,
        });
        modalRef.componentInstance.objectId = project.uuid;
        modalRef.componentInstance.presetDocumentType = 'PO';
        modalRef.componentInstance.presetDescription = `Purchase Order voor ${project.title || 'dit project'}`;

        modalRef.result.then(
            () => this.refreshPoDocumentsAfterAdd(project.uuid, knownPoFileIds),
            () => {
                this.selectedPoFileId = this.selectedPoDoc?.fileId || '';
                this.updateCreateDisabled();
            },
        );
    }

    private refreshPoDocumentsAfterAdd(projectId: string, knownPoFileIds: Set<string>): void {
        this.subscriptions.add(
            this.track(this.globalFilesService.getAllFilesLinkedToObject(projectId)).subscribe({
                next: (payload) => {
                    const linkedFiles = Array.isArray(payload?.linkedFiles) ? payload.linkedFiles : [];
                    const newPoDocument = linkedFiles.find(
                        (file) =>
                            (file?.fileType || '').toLowerCase() === 'uploadeddocument' &&
                            (file.documentType || '').toUpperCase() === 'PO' &&
                            !knownPoFileIds.has(file.fileId),
                    );

                    if (!newPoDocument?.fileId) {
                        this.loadProjectDocuments();
                        return;
                    }

                    this.selectedPoFileId = newPoDocument.fileId;
                    this.toastr.info('PO-document toegevoegd. AI extractie wordt gestart.');
                    this.subscriptions.add(
                        this.track(this.globalFilesService.extractProjectDocument(newPoDocument.fileId)).subscribe({
                            next: () => {
                                this.toastr.success('AI extractie voltooid');
                                this.loadProjectDocuments();
                            },
                            error: (error) => {
                                this.logger.warn('InvoiceDialog: Failed to extract newly added PO document', error);
                                this.toastr.warning('PO-document toegevoegd, maar AI extractie is niet gelukt. Je kan dit nog uitvoeren via de Files tab.');
                                this.loadProjectDocuments();
                            },
                        }),
                    );
                },
                error: (error) => {
                    this.logger.warn('InvoiceDialog: Failed to refresh PO documents after add', error);
                    this.loadProjectDocuments();
                },
            }),
        );
    }

    onSesDocumentSelected(fileId: string): void {
        if (fileId === this.addNewSesDocumentValue) {
            this.selectedSesFileId = this.selectedSesDoc?.fileId || '';
            this.openAddSesDocumentModal();
            this.updateCreateDisabled();
            return;
        }

        const match = this.sesDocuments.find((d) => d.value === fileId);
        this.sesNumber = match?.number || '';
        this.selectedSesDoc = match?.doc || null;
        if (this.selectedSesDoc) {
            this.applySelectedSesData(this.selectedSesDoc);
            this.applySelectedSesToTimeslots();
            this.loadTimesheetsForSelectedSesPeriod();
            this.enrichSelectedSesDocument();
        }
        this.updateCreateDisabled();
    }

    public openAddSesDocumentModal(): void {
        const project = this.project?.uuid ? this.project : this.getInvoiceProjects()[0];
        if (!project?.uuid) {
            this.toastr.error('Geen project gevonden om het SES-document aan te linken.');
            return;
        }

        const knownSesFileIds = new Set((this.sesDocuments || []).map((doc) => doc.value).filter((value) => !!value));
        const modalRef = this.modalService.open(AddFileToObjectModalComponent, {
            windowClass: 'modal-prompt modal-above-mat-dialog',
            backdropClass: 'modal-above-mat-dialog-backdrop',
            animation: false,
        });
        modalRef.componentInstance.objectId = project.uuid;
        modalRef.componentInstance.presetDocumentType = 'SES';
        modalRef.componentInstance.presetDescription = `Toelating tot facturatie (SES) voor ${project.title || 'dit project'}`;

        modalRef.result.then(
            () => this.refreshSesDocumentsAfterAdd(project.uuid, knownSesFileIds),
            () => {
                this.selectedSesFileId = this.selectedSesDoc?.fileId || '';
                this.updateCreateDisabled();
            },
        );
    }

    private refreshSesDocumentsAfterAdd(projectId: string, knownSesFileIds: Set<string>): void {
        this.subscriptions.add(
            this.track(this.globalFilesService.getAllFilesLinkedToObject(projectId)).subscribe({
                next: (payload) => {
                    const linkedFiles = Array.isArray(payload?.linkedFiles) ? payload.linkedFiles : [];
                    const newSesDocument = linkedFiles.find(
                        (file) =>
                            (file?.fileType || '').toLowerCase() === 'uploadeddocument' &&
                            (file.documentType || '').toUpperCase() === 'SES' &&
                            !knownSesFileIds.has(file.fileId),
                    );

                    if (!newSesDocument?.fileId) {
                        this.loadProjectDocuments();
                        return;
                    }

                    this.selectedSesFileId = newSesDocument.fileId;
                    this.toastr.info('SES-document toegevoegd. AI extractie wordt gestart.');
                    this.subscriptions.add(
                        this.track(this.globalFilesService.extractProjectDocument(newSesDocument.fileId)).subscribe({
                            next: () => {
                                this.toastr.success('AI extractie voltooid');
                                this.loadProjectDocuments();
                            },
                            error: (error) => {
                                this.logger.warn('InvoiceDialog: Failed to extract newly added SES document', error);
                                this.toastr.warning('SES-document toegevoegd, maar AI extractie is niet gelukt. Je kan dit nog uitvoeren via de Files tab.');
                                this.loadProjectDocuments();
                            },
                        }),
                    );
                },
                error: (error) => {
                    this.logger.warn('InvoiceDialog: Failed to refresh SES documents after add', error);
                    this.loadProjectDocuments();
                },
            }),
        );
    }

    private getSesDocumentReference(doc: LinkedFileToObject | null | undefined): string {
        return ((doc?.extractedSesNumber || doc?.extractedFileNumber || '') as string).trim();
    }

    public getSelectedSesReferenceLabel(): string {
        return this.getSesDocumentReference(this.selectedSesDoc) || this.sesNumber || '-';
    }

    private buildSesDocumentOptionLabel(doc: LinkedFileToObject, matchesPeriod: boolean): string {
        const sesRef = this.getSesDocumentReference(doc);
        const fileRef = (doc.extractedFileNumber || '').trim();
        const displayName = this.getDocumentDisplayName(doc);
        const periodLabel = this.getDocumentPeriodLabel(doc);
        const refs = [sesRef ? `SES ${sesRef}` : '', fileRef && fileRef !== sesRef ? `file ${fileRef}` : ''].filter((value) => value.length > 0).join(' / ');

        return `${refs ? `${refs} — ` : ''}${displayName}${periodLabel ? ` (${periodLabel})` : ''}${matchesPeriod ? '' : ' · buiten huidige periode'}`;
    }

    public hasSesReference(): boolean {
        return !!(this.sesNumber || this.getSesDocumentReference(this.selectedSesDoc)).trim();
    }

    private getBestSesDocumentOption(): { value: string; number: string; label: string; doc: LinkedFileToObject; matchesPeriod: boolean } | null {
        if (!this.sesDocuments.length) {
            return null;
        }
        if (this.selectedSesFileId) {
            const selected = this.sesDocuments.find((doc) => doc.value === this.selectedSesFileId);
            if (selected) return selected;
        }
        const matching = this.sesDocuments.find((doc) => doc.matchesPeriod);
        return matching || (this.sesDocuments.length === 1 ? this.sesDocuments[0] : null);
    }

    private applySelectedSesData(sesDoc: LinkedFileToObject): void {
        const sesRef = this.getSesDocumentReference(sesDoc);
        if (sesRef) {
            this.sesNumber = sesRef;
        }

        // SES references a PO number. In SES mode the SES document is leading,
        // so overwrite stale contract/account PO values with the PO from SES.
        const sesPo = (sesDoc.extractedPoNumber || '').trim();
        if (sesPo && (this.invoiceMode === 'ses' || !(this.poNumber || '').trim())) {
            this.poNumber = sesPo;
            // Try to find matching PO document
            const matchingPo = this.poDocuments.find((d) => (d.doc.extractedPoNumber || '').trim() === sesPo);
            if (matchingPo) {
                this.selectedPoFileId = matchingPo.value;
                this.selectedPoDoc = matchingPo.doc;
            }
        }
    }

    private enrichSelectedSesDocument(): void {
        if (!this.selectedSesDoc?.fileId) {
            return;
        }

        this.subscriptions.add(
            this.track(this.globalFilesService.fetchUploadedDocumentById(this.selectedSesDoc.fileId)).subscribe({
                next: (document) => {
                    if (!this.selectedSesDoc || document.documentId !== this.selectedSesDoc.fileId) {
                        return;
                    }
                    this.applyUploadedDocumentDataToLinkedFile(this.selectedSesDoc, document);
                    const option = this.sesDocuments.find((doc) => doc.value === this.selectedSesDoc.fileId);
                    if (option) {
                        option.number = this.getSesDocumentReference(this.selectedSesDoc);
                        option.label = this.buildSesDocumentOptionLabel(this.selectedSesDoc, option.matchesPeriod);
                        option.doc = this.selectedSesDoc;
                    }
                    this.applySelectedSesData(this.selectedSesDoc);
                    this.applySelectedSesToTimeslots();
                    this.loadTimesheetsForSelectedSesPeriod();
                    this.updateCreateDisabled();
                },
                error: (error) => {
                    this.logger.warn('InvoiceDialog: Failed to enrich selected SES document', error);
                },
            }),
        );
    }

    private applyUploadedDocumentDataToLinkedFile(linkedFile: LinkedFileToObject, document: UploadedDocument): void {
        const data = document.extractedData || {};
        linkedFile.documentType = document.documentType || linkedFile.documentType;
        linkedFile.extractionStatus = document.extractionStatus || linkedFile.extractionStatus;
        linkedFile.extractedData = data;
        linkedFile.extractedPoNumber =
            this.firstText(document.extractedPoNumber, data, ['poNumber', 'po_number', 'purchaseOrderNumber', 'purchase_order_number', 'PO']) ||
            linkedFile.extractedPoNumber;
        linkedFile.extractedSesNumber =
            this.firstText(document.extractedSesNumber, data, ['sesNumber', 'ses_number', 'serviceEntrySheetNumber', 'service_entry_sheet_number', 'SES']) ||
            linkedFile.extractedSesNumber;
        linkedFile.extractedFileNumber =
            this.firstText(document.extractedFileNumber, data, [
                'fileNumber',
                'file_number',
                'fileNo',
                'file_no',
                'documentNumber',
                'document_number',
                'referenceNumber',
                'reference_number',
            ]) || linkedFile.extractedFileNumber;
        linkedFile.extractedAmount =
            this.firstNumber(document.extractedAmount, data, [
                'amount',
                'totalAmount',
                'total_amount',
                'totalPrice',
                'total_price',
                'approvedAmount',
                'approved_amount',
                'value',
            ]) ?? linkedFile.extractedAmount;
        linkedFile.extractedHours =
            this.firstNumber(document.extractedHours, data, [
                'hours',
                'approvedHours',
                'approved_hours',
                'totalHours',
                'total_hours',
                'approvedServiceHours',
                'quantity',
            ]) ?? linkedFile.extractedHours;
        linkedFile.extractedUnitRate =
            this.firstNumber(document.extractedUnitRate, data, [
                'unitRate',
                'unit_rate',
                'dayRate',
                'day_rate',
                'dailyRate',
                'hourlyRate',
                'hourly_rate',
                'rate',
            ]) ?? linkedFile.extractedUnitRate;
        linkedFile.extractedCurrency = this.firstText(document.extractedCurrency, data, ['currency', 'Currency']) || linkedFile.extractedCurrency;
        linkedFile.extractedPeriodFrom =
            document.extractedPeriodFrom ||
            this.firstText(null, data, ['periodFrom', 'period_from', 'startDate', 'start_date', 'validFrom']) ||
            linkedFile.extractedPeriodFrom;
        linkedFile.extractedPeriodTo =
            document.extractedPeriodTo ||
            this.firstText(null, data, ['periodTo', 'period_to', 'endDate', 'end_date', 'validTo']) ||
            linkedFile.extractedPeriodTo;
    }

    private firstText(primary: any, data: Record<string, any>, keys: string[]): string {
        if (primary !== null && primary !== undefined && String(primary).trim().length > 0) {
            return String(primary).trim();
        }
        for (const key of keys) {
            const value = this.findNestedValue(data, key);
            if (value !== null && value !== undefined && String(value).trim().length > 0) {
                return String(value).trim();
            }
        }
        return '';
    }

    private firstNumber(primary: any, data: Record<string, any>, keys: string[]): number | null {
        const primaryNumber = this.parseExtractedNumber(primary);
        if (primaryNumber !== null) {
            return primaryNumber;
        }
        for (const key of keys) {
            const parsed = this.parseExtractedNumber(this.findNestedValue(data, key));
            if (parsed !== null) {
                return parsed;
            }
        }
        return null;
    }

    private findNestedValue(source: any, key: string): any {
        if (!source || typeof source !== 'object') {
            return null;
        }
        if (Object.prototype.hasOwnProperty.call(source, key)) {
            return source[key];
        }
        const normalizedKey = key.toLowerCase().replace(/[_\s-]/g, '');
        for (const [entryKey, entryValue] of Object.entries(source)) {
            if (entryKey.toLowerCase().replace(/[_\s-]/g, '') === normalizedKey) {
                return entryValue;
            }
            if (entryValue && typeof entryValue === 'object') {
                const nested = this.findNestedValue(entryValue, key);
                if (nested !== null && nested !== undefined) {
                    return nested;
                }
            }
        }
        return null;
    }

    private parseExtractedNumber(value: any): number | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }
        if (typeof value === 'number') {
            return Number.isFinite(value) ? value : null;
        }

        let text = String(value).trim();
        if (!text) {
            return null;
        }
        text = text.replace(/[^\d,.-]/g, '');
        if (!text || text === '-' || text === ',' || text === '.') {
            return null;
        }

        const lastComma = text.lastIndexOf(',');
        const lastDot = text.lastIndexOf('.');
        if (lastComma >= 0 && lastDot >= 0) {
            const decimalSeparator = lastComma > lastDot ? ',' : '.';
            const thousandsSeparator = decimalSeparator === ',' ? '.' : ',';
            text = text.split(thousandsSeparator).join('');
            text = text.replace(decimalSeparator, '.');
        } else if (lastComma >= 0) {
            const decimals = text.length - lastComma - 1;
            text = decimals > 0 && decimals <= 2 ? text.replace(',', '.') : text.replace(/,/g, '');
        } else if (lastDot >= 0) {
            const decimals = text.length - lastDot - 1;
            text = decimals > 0 && decimals <= 2 ? text : text.replace(/\./g, '');
        }

        const parsed = Number(text);
        return Number.isFinite(parsed) ? parsed : null;
    }

    private applySelectedSesToTimeslots(): void {
        if (this.invoiceMode !== 'ses' || !this.selectedSesDoc) {
            return;
        }

        const from = this.selectedSesDoc.extractedPeriodFrom;
        const to = this.selectedSesDoc.extractedPeriodTo;
        if (from && to) {
            this.worklogsFrom = from;
            this.worklogsTo = to;
        }

        // Keep BY_SES on the same source of truth as BY_TIME: the project
        // timeslots passed into this dialog. Do not rebuild the selection from
        // Jira/account worklogs here; those can be incomplete for SES and led to
        // 0 logged hours while the invoice list showed hours correctly.
        //
        // SES invoices can intentionally be created for a project/period that
        // already has an invoice. The SES document is leading, so we keep billed
        // slots selectable here and still validate the logged hours against SES.
        const allSlots = this.getProjectTimeslotsForInvoiceSelection();

        this.selectedTimeslots = allSlots.filter((slot) => this.isTimeslotInSelectedSesPeriod(slot));
        this.applySelectedSesAllocationDefaults();
        this.updateCreateDisabled();
    }

    private applySelectedSesAllocationDefaults(): void {
        const slots = this.selectedTimeslots || [];
        const approvedHours = this.getSelectedSesApprovedHours();
        const approvedMinutes = approvedHours !== null ? approvedHours * 60 : null;
        let remainingApprovedMinutes = approvedMinutes;

        slots.forEach((slot) => {
            const remainingSlotMinutes = this.getRemainingInvoiceMinutesForTimeslot(slot);
            const allocatedMinutes =
                remainingApprovedMinutes === null ? remainingSlotMinutes : Math.max(0, Math.min(remainingSlotMinutes, remainingApprovedMinutes));
            (slot as any)._sesAllocatedMinutes = Number(allocatedMinutes.toFixed(2));
            if (remainingApprovedMinutes !== null) {
                remainingApprovedMinutes = Math.max(0, remainingApprovedMinutes - allocatedMinutes);
            }
        });
    }

    private isTimeslotInSelectedSesPeriod(timeslot: Timeslot): boolean {
        if (!this.selectedSesDoc?.extractedPeriodFrom && !this.selectedSesDoc?.extractedPeriodTo) {
            return true;
        }
        const date = this.getTimeslotInvoicePeriodDate(timeslot);
        if (!date) return false;
        const from = this.selectedSesDoc.extractedPeriodFrom;
        const to = this.selectedSesDoc.extractedPeriodTo;
        if (from && date < from) return false;
        if (to && date > to) return false;
        return true;
    }

    private getTimeslotInvoicePeriodDate(timeslot: Timeslot): string | null {
        const parentTimesheet = this.findTimesheetForSlot(timeslot);
        const embeddedTimesheet = timeslot?.timesheet;
        const candidates = [
            parentTimesheet?.expirationDate,
            embeddedTimesheet?.expirationDate,
            (timeslot as any)?.validatedPeriodISO,
            this.getMonthStartFromTimeslotPdf(timeslot),
            this.invoiceDate,
            timeslot?.createdOn,
        ];

        for (const candidate of candidates) {
            const normalized = this.normalizeDateCandidate(candidate);
            if (normalized) {
                return normalized;
            }
        }

        return null;
    }

    private normalizeDateCandidate(candidate: any): string | null {
        if (!candidate) {
            return null;
        }
        if (candidate instanceof Date) {
            const parsedDate = moment(candidate);
            return parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD') : null;
        }

        const text = String(candidate).trim();
        if (!text) {
            return null;
        }
        const monthOnly = text.match(/^(\d{4})-(\d{2})$/);
        if (monthOnly) {
            return `${monthOnly[1]}-${monthOnly[2]}-01`;
        }
        const parsed = moment(text);
        return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
    }

    private getMonthStartFromTimeslotPdf(timeslot: Timeslot): string | null {
        const fileName = this.extractFileName(timeslot?.pdfUrl || '');
        const match = fileName.match(/(\d{4})-(\d{2})/);
        return match ? `${match[1]}-${match[2]}-01` : null;
    }

    public getSelectedSesLoggedHours(): number {
        return Number((this.selectedTimeslots || []).reduce((sum, slot) => sum + this.getSesAllocatedMinutesForTimeslot(slot) / 60, 0).toFixed(2));
    }

    public getSesAllocatedHoursForTimeslot(slot: Timeslot): number {
        return Number((this.getSesAllocatedMinutesForTimeslot(slot) / 60).toFixed(2));
    }

    public onSesAllocatedHoursChanged(slot: Timeslot, value: any): void {
        const parsed = Number(String(value ?? '').replace(',', '.'));
        const maxMinutes = this.getRemainingInvoiceMinutesForTimeslot(slot);
        const minutes = Number.isFinite(parsed) && parsed > 0 ? Math.min(parsed * 60, maxMinutes) : 0;
        (slot as any)._sesAllocatedMinutes = Number(minutes.toFixed(2));
        this.updateCreateDisabled();
    }

    private getSesAllocatedMinutesForTimeslot(slot: Timeslot): number {
        const explicit = Number((slot as any)?._sesAllocatedMinutes);
        if (Number.isFinite(explicit)) {
            return Math.max(0, explicit);
        }
        return this.getRemainingInvoiceMinutesForTimeslot(slot);
    }

    private getRemainingInvoiceMinutesForTimeslot(slot: Timeslot): number {
        const total = Number(slot?.minutes || 0);
        if (this.invoiceMode === 'ses') {
            return Math.max(0, total);
        }
        const allocated = this.getAllocatedMinutesForTimeslot(slot);
        return Math.max(0, total - allocated);
    }

    public getSelectedSesApprovedHours(): number | null {
        const value = this.firstNumber((this.selectedSesDoc as any)?.extractedHours, this.selectedSesDoc?.extractedData || {}, [
            'hours',
            'approvedHours',
            'approved_hours',
            'totalHours',
            'total_hours',
            'approvedServiceHours',
            'quantity',
        ]);
        return value !== null && value > 0 ? value : null;
    }

    public getSelectedSesAmount(): number {
        const value = this.firstNumber((this.selectedSesDoc as any)?.extractedAmount, this.selectedSesDoc?.extractedData || {}, [
            'amount',
            'totalAmount',
            'total_amount',
            'totalPrice',
            'total_price',
            'approvedAmount',
            'approved_amount',
            'value',
        ]);
        return value !== null && value > 0 ? value : 0;
    }

    public getSelectedSesUnitRate(): number | null {
        const amount = this.getSelectedSesAmount();
        const hours = this.getSelectedSesApprovedHours();
        if (amount > 0 && hours !== null && hours > 0) {
            return Number((amount / hours).toFixed(4));
        }

        const value = this.firstNumber((this.selectedSesDoc as any)?.extractedUnitRate, this.selectedSesDoc?.extractedData || {}, [
            'unitRate',
            'unit_rate',
            'hourlyRate',
            'hourly_rate',
            'rate',
            'dayRate',
            'day_rate',
            'dailyRate',
        ]);
        return value !== null && value > 0 ? value : null;
    }

    public getSelectedSesProjectTitles(): string[] {
        const titles = new Set<string>();
        (this.selectedTimeslots || []).forEach((slot) => {
            const title = (slot?.project?.title || '').trim();
            if (title) titles.add(title);
        });
        return Array.from(titles);
    }

    public getSelectedSesHoursDifference(): number | null {
        const approved = this.getSelectedSesApprovedHours();
        if (approved === null) return null;
        return Number((this.getSelectedSesLoggedHours() - approved).toFixed(2));
    }

    public isSelectedSesHoursMatch(): boolean | null {
        const diff = this.getSelectedSesHoursDifference();
        if (diff === null) return null;
        return Math.abs(diff) <= 0.25;
    }

    private getInvoiceDocumentRange(): { from: string; to: string } {
        return this.getWorklogRange();
    }

    private documentOverlapsRange(doc: LinkedFileToObject, rangeFrom: string, rangeTo: string): boolean {
        if (!doc?.extractedPeriodFrom && !doc?.extractedPeriodTo) return true;

        const docFrom = doc.extractedPeriodFrom || doc.extractedPeriodTo;
        const docTo = doc.extractedPeriodTo || doc.extractedPeriodFrom;
        if (!docFrom || !docTo) return false;

        return docFrom <= rangeTo && docTo >= rangeFrom;
    }

    private getDocumentPeriodLabel(doc: LinkedFileToObject): string {
        if (!doc?.extractedPeriodFrom && !doc?.extractedPeriodTo) return '';
        return `${doc.extractedPeriodFrom || '?'} - ${doc.extractedPeriodTo || '?'}`;
    }

    private loadTimesheetsForSelectedSesPeriod(): void {
        if (this.invoiceMode !== 'ses' || !this.selectedSesDoc || !this.project?.uuid) {
            return;
        }
        const projectId = this.project.uuid;
        const from = this.selectedSesDoc.extractedPeriodFrom;
        const to = this.selectedSesDoc.extractedPeriodTo;
        if (!from || !to) {
            return;
        }

        const months: any[] = [];
        const cursor = moment(from, 'YYYY-MM-DD').startOf('month');
        const end = moment(to, 'YYYY-MM-DD').startOf('month');
        while (cursor.isSameOrBefore(end)) {
            months.push(cursor.clone());
            cursor.add(1, 'month');
        }

        const loadedMonths = new Set(
            [
                ...this.getAllTimesheets().map((timesheet) => moment(timesheet.expirationDate).format('YYYY-MM')),
                ...this.getProjectTimeslotsForInvoiceSelection().map((slot) => moment(slot?.timesheet?.expirationDate || slot?.createdOn).format('YYYY-MM')),
            ].filter((value) => !!value && value !== 'Invalid date'),
        );
        const requests = months
            .filter((month) => !loadedMonths.has(month.format('YYYY-MM')))
            .map((month) =>
                this.timesheetsService.fetchTimesheetsByProjectIdAndYearAndMonth(projectId, month.year(), month.month() + 1).pipe(
                    catchError((error) => {
                        this.logger.warn(`InvoiceDialog: Failed to fetch SES timesheets for ${month.format('YYYY-MM')}`, error);
                        return of([] as Timesheet[]);
                    }),
                ),
            );

        if (requests.length === 0) {
            this.applySelectedSesToTimeslots();
            return;
        }

        this.subscriptions.add(
            this.track(forkJoin(requests)).subscribe((results) => {
                const existingIds = new Set(this.getAllTimesheets().map((timesheet) => timesheet.uuid));
                const fetched = results.reduce((acc: Timesheet[], timesheets) => {
                    (timesheets || []).forEach((timesheet) => {
                        if (timesheet?.uuid && !existingIds.has(timesheet.uuid)) {
                            existingIds.add(timesheet.uuid);
                            acc.push(timesheet);
                        }
                    });
                    return acc;
                }, []);
                this.previousTimesheets = [...(this.previousTimesheets || []), ...fetched];
                this.applySelectedSesToTimeslots();
            }),
        );
    }

    /** Calculate the invoice total (excl. VAT) for verification against PO/SES amounts */
    public calculateInvoiceTotalExclVat(): number {
        if (this.invoiceMode === 'account') {
            return this.calculateAccountTotalWithPM();
        }
        if (this.invoiceMode === 'ses') {
            return this.getSelectedSesAmount();
        }
        // Time mode: sum hours × rate for selected timeslots
        let total = 0;
        (this.selectedTimeslots || []).forEach((timeslot) => {
            const hours = (timeslot as any)?._editedHours ?? this.calculateWorkedHoursForTimeslot(timeslot);
            const rate = (timeslot as any)?._editedRate ?? (this.getPricePerHourForTimeslot(timeslot) || 0);
            total += hours * rate;
        });
        if (this.addProjectManagementFee) {
            total += total * (this.projectManagementPercentage / 100);
        }
        return +total.toFixed(2);
    }

    /** Check if the invoice date falls within the PO/SES period */
    public isInvoiceDateInPeriod(doc: LinkedFileToObject | null): boolean | null {
        if (!doc || (!doc.extractedPeriodFrom && !doc.extractedPeriodTo)) return null;
        const invoiceDate = this.invoiceDate;
        if (!invoiceDate) return null;
        if (doc.extractedPeriodFrom && invoiceDate < doc.extractedPeriodFrom) return false;
        if (doc.extractedPeriodTo && invoiceDate > doc.extractedPeriodTo) return false;
        return true;
    }

    /** Check if the invoice amount is within the document's extracted amount */
    public isAmountWithinBudget(doc: LinkedFileToObject | null): boolean | null {
        if (!doc || doc.extractedAmount == null) return null;
        const invoiceTotal = this.calculateInvoiceTotalExclVat();
        return invoiceTotal <= doc.extractedAmount;
    }

    private getDocumentDisplayName(doc: LinkedFileToObject): string {
        if (doc.description && doc.description.trim().length > 0) {
            return doc.description.trim();
        }
        // Extract filename from S3 URL title
        try {
            const url = doc.title || '';
            const parts = url.split('/');
            const fileName = decodeURIComponent(parts[parts.length - 1] || '');
            return fileName || doc.title || 'Document';
        } catch {
            return doc.title || 'Document';
        }
    }

    private resolveJiraReference(): string {
        try {
            // Primary: project detail
            let id = ((this.project as any)?.jiraProjectId ?? '').toString().trim();
            if (id) return id;
            // Fallback: timeslot project's jiraProjectId
            const slotWithId = (this.timeslots || []).find((ts) => (ts as any)?.project?.jiraProjectId);
            if (slotWithId?.project?.jiraProjectId) {
                id = String(slotWithId.project.jiraProjectId).trim();
                if (id) return id;
            }
            // Fallback: unique projects list
            const uniqueWithId = (this.uniqueProjects || []).find((p) => (p as any)?.jiraProjectId);
            if (uniqueWithId && (uniqueWithId as any).jiraProjectId) {
                id = String((uniqueWithId as any).jiraProjectId).trim();
                if (id) return id;
            }
            // Last resort: if we only have a numeric raw key, treat it as id
            const raw = (this.jiraRawKey || '').trim();
            if (raw && /^\d+$/.test(raw)) return raw;
            return '';
        } catch {
            return '';
        }
    }

    private fetchTempoAccountsForCurrentProject(): void {
        try {
            const projectIdStr = (this.jiraReferenceKey || '').trim();
            // Skip when Jira project id/key is missing or invalid
            if (!projectIdStr) {
                return;
            }
            // If numeric, ensure > 0 (avoid calling /project/0)
            const isNumeric = /^\d+$/.test(projectIdStr);
            if (isNumeric && Number(projectIdStr) <= 0) {
                return;
            }
            // Use backend endpoints to avoid CORS issues in browser
            this.subscriptions.add(
                this.track(this.jiraLoginService.getTempoAccountLinksForProject(projectIdStr)).subscribe(
                    (links: any[]) => {
                        const normalized = (links || [])
                            .map((l) => ({
                                id: String(l?.accountId || '').trim(),
                                key: String(l?.accountKey || '').trim(),
                                name: String(l?.accountName || '').trim(),
                            }))
                            .filter((a) => !!a.id);

                        const uniqueAccountIds = Array.from(new Set(normalized.map((a) => a.id)));
                        if (uniqueAccountIds.length === 0) {
                            this.tempoAccounts = [];
                            return;
                        }

                        const detailCalls = uniqueAccountIds.map((id) => this.jiraLoginService.getTempoAccountDetails(id));
                        this.subscriptions.add(
                            this.track(forkJoin(detailCalls)).subscribe(
                                (details: any[]) => {
                                    // Merge links and details: prefer details for key/name/status
                                    const byId = new Map<string, { id: string; key?: string; name?: string; status?: string }>();
                                    normalized.forEach((a) => byId.set(a.id, a));
                                    const enriched = (details || []).map((d) => ({
                                        id: String(d.id || ''),
                                        key: d.key || byId.get(String(d.id || ''))?.key,
                                        name: d.name || byId.get(String(d.id || ''))?.name,
                                        status: d.status || '',
                                    }));
                                    this.tempoAccounts = enriched;
                                    // After accounts are loaded, fetch per-account totals for the current month
                                    this.loadWorklogTotalsForAccounts();
                                },
                                () => {
                                    // Fallback: keep normalized without status
                                    this.tempoAccounts = normalized;
                                    this.loadWorklogTotalsForAccounts();
                                },
                            ),
                        );
                    },
                    () => {
                        this.tempoAccounts = [];
                        this.accountWorklogDurationLabelById = {};
                    },
                ),
            );
        } catch {
            // noop
        }
    }

    private formatDurationFromSeconds(totalSeconds: number): string {
        if (!totalSeconds || totalSeconds <= 0) return '0h';
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        if (minutes > 0) {
            return `${hours}h ${minutes}m`;
        }
        return `${hours}h`;
    }

    private loadWorklogTotalsForAccounts(): void {
        try {
            const withKey = (this.tempoAccounts || []).filter((a) => !!a.key);
            if (withKey.length === 0) {
                this.accountWorklogDurationLabelById = {};
                this.accountWorklogSecondsById = {};
                return;
            }
            const { from, to } = this.getWorklogRange();
            const calls = withKey.map((a) => this.jiraLoginService.getTempoWorklogsByAccount(a.key!, from, to, 1000).pipe(catchError(() => of([]))));
            this.subscriptions.add(
                this.track(forkJoin(calls)).subscribe((results: any[][]) => {
                    const mapLabel: Record<string, string> = {};
                    const mapSeconds: Record<string, number> = {};
                    results.forEach((logs, idx) => {
                        const acc = withKey[idx];
                        const totalSeconds = (Array.isArray(logs) ? logs : []).reduce((sum, wl) => sum + Number(wl?.billableSeconds || 0), 0);
                        mapLabel[acc.id] = this.formatDurationFromSeconds(totalSeconds);
                        mapSeconds[acc.id] = totalSeconds;
                    });
                    this.accountWorklogDurationLabelById = mapLabel;
                    this.accountWorklogSecondsById = mapSeconds;

                    // Auto-select the account with most hours if none selected yet
                    try {
                        if ((this.selectedTempoAccountIds || []).length === 0) {
                            let maxId: string | null = null;
                            let maxSeconds = 0;
                            Object.entries(this.accountWorklogSecondsById).forEach(([id, secs]) => {
                                if ((secs || 0) > maxSeconds) {
                                    maxSeconds = secs || 0;
                                    maxId = id;
                                }
                            });
                            if (maxId && maxSeconds > 0) {
                                const acc = withKey.find((a) => a.id === maxId);
                                if (acc) {
                                    this.selectedTempoAccountIds = [acc.id];
                                    this.loadWorklogsForSelectedAccounts();
                                    this.refreshInvoiceFieldsByMode();
                                }
                            }
                        }
                    } catch {
                        /* noop */
                    }
                }),
            );
        } catch {
            this.accountWorklogDurationLabelById = {};
            this.accountWorklogSecondsById = {};
        }
    }

    formatTempoAccountLabel(a: { id: string; key?: string; name?: string; status?: string }): string {
        try {
            const key = (a.key || a.id || '').toString();
            const name = (a.name || '').toString();
            const closed = a.status?.toUpperCase() === 'CLOSED' ? ' (CLOSED)' : '';
            const dur = this.accountWorklogDurationLabelById[a.id];
            const durPart = dur ? ` (${dur})` : '';
            const base = key ? (name ? `${key} (${name})` : key) : name || '';
            return `${base}${closed}${durPart}`;
        } catch {
            return a.name || a.key || a.id;
        }
    }

    onWorklogDateRangeChanged(): void {
        // Recompute totals and reload selected account when date range changes
        this.loadWorklogTotalsForAccounts();
        if ((this.selectedTempoAccountIds || []).length > 0) {
            this.loadWorklogsForSelectedAccounts();
        }
        this.loadPreviousUnbilledTimeslots();
        this.loadProjectDocuments();
        this.refreshInvoiceFieldsByMode();
    }

    onAdditionalMonthsChanged(): void {
        this.additionalMonthsBefore = this.normalizeAdditionalMonths(this.additionalMonthsBefore);
        this.additionalMonthsAfter = this.normalizeAdditionalMonths(this.additionalMonthsAfter);
        this.loadPreviousUnbilledTimeslots();
    }

    private normalizeAdditionalMonths(value: any): number {
        const numeric = Number(value);
        if (!Number.isFinite(numeric) || numeric <= 0) {
            return 0;
        }
        return Math.min(12, Math.floor(numeric));
    }

    private resolveJiraRawKey(): string {
        try {
            // First: if we have a projectId and a loaded mapping list, derive the textual key from it
            const id = (this.jiraReferenceKey || '').trim();
            if (id && Array.isArray(this.jiraProjectKeyList$) && this.jiraProjectKeyList$.length > 0) {
                const byId = this.jiraProjectKeyList$.find((c) => (c?.projectId || '') === id);
                const mappedKey = (byId?.jiraKey || '').trim();
                if (mappedKey) return mappedKey;
            }

            // Fallbacks: use textual keys present on project/timeslots/uniqueProjects (ignore numeric-only values)
            const fromProject = ((this.project as any)?.jiraProjectKey || '').toString().trim();
            if (fromProject) {
                if (!/^\d+$/.test(fromProject)) {
                    return fromProject;
                }
            }

            const slotWithKey = (this.timeslots || []).find((ts) => (ts as any)?.project?.jiraProjectKey);
            if (slotWithKey?.project?.jiraProjectKey) {
                const k = String(slotWithKey.project.jiraProjectKey).trim();
                if (!/^\d+$/.test(k)) {
                    return k;
                }
            }

            const unique = (this.uniqueProjects || []).find((p) => (p as any)?.jiraProjectKey);
            if (unique) {
                const k = String((unique as any).jiraProjectKey).trim();
                if (!/^\d+$/.test(k)) {
                    return k;
                }
            }
            return '';
        } catch {
            return '';
        }
    }

    private updateJiraRawKeyFromList(): void {
        try {
            let id = (this.jiraReferenceKey || '').trim();
            if (!id) {
                const raw = (this.jiraRawKey || '').trim();
                if (raw && /^\d+$/.test(raw)) {
                    id = raw;
                }
            }
            if (!id) return;
            if (!Array.isArray(this.jiraProjectKeyList$) || this.jiraProjectKeyList$.length === 0) return;
            const byId = this.jiraProjectKeyList$.find((c) => String(c?.projectId || '').trim() === id);
            const mappedKey = (byId?.jiraKey || '').trim();
            if (mappedKey) {
                this.jiraRawKey = mappedKey;
            }
        } catch {
            // noop
        }
    }

    private updateJiraReferenceIdFromList(): void {
        try {
            // If we already have a numeric id, keep it
            const currentId = (this.jiraReferenceKey || '').trim();
            if (currentId && /^\d+$/.test(currentId)) {
                return;
            }
            // Try to map textual key -> numeric id from loaded project list
            const key = (this.jiraRawKey || '').trim();
            if (!key) return;
            if (!Array.isArray(this.jiraProjectKeyList$) || this.jiraProjectKeyList$.length === 0) return;
            const match = this.jiraProjectKeyList$.find((c) => (c?.jiraKey || '') === key);
            const id = (match?.projectId || '').trim();
            if (id) {
                this.jiraReferenceKey = id;
            }
        } catch {
            // noop
        }
    }

    private updateJiraReferenceName(): void {
        try {
            const id = (this.jiraReferenceKey || '').trim();
            const key = (this.jiraRawKey || '').trim();
            if (!Array.isArray(this.jiraProjectKeyList$) || this.jiraProjectKeyList$.length === 0) {
                this.jiraReferenceName = '';
                return;
            }
            // Prefer matching by textual Jira key if available
            let match = key ? this.jiraProjectKeyList$.find((c) => (c?.jiraKey || '') === key) : undefined;
            if (!match && id) {
                // Fallback: match by numeric project id if provided (coerce to string)
                match = this.jiraProjectKeyList$.find((c) => String(c?.projectId || '').trim() === id);
            }
            this.jiraReferenceName = match?.projectName || '';
        } catch {
            this.jiraReferenceName = '';
        }
    }

    private computeReferenceDisplay(): string {
        try {
            // Preserve original department casing (no forced uppercase)
            const type = (this.project?.projectType || '').toString();
            const key = this.jiraRawKey || '';
            const cleanedType = type.trim();
            const cleanedKey = (key || '').toString().trim();
            const selectedAccountKeys =
                this.invoiceMode === 'account'
                    ? this.getSelectedTempoAccounts()
                          .map((a) => (a.key || '').trim())
                          .filter((k) => !!k)
                    : [];
            const accSuffix = selectedAccountKeys.length > 0 ? `/${selectedAccountKeys.join('+')}` : '';
            if (cleanedType && cleanedKey) {
                return `${cleanedType}/${cleanedKey}${accSuffix}`;
            }
            const base = cleanedKey || cleanedType || '';
            return base ? `${base}${accSuffix}` : '';
        } catch {
            return '';
        }
    }

    private getAllTimesheets(): Timesheet[] {
        return [...(this.timesheets || []), ...(this.previousTimesheets || [])];
    }

    private getProjectTimeslotsForInvoiceSelection(): Timeslot[] {
        const projectId = this.project?.uuid || this.timeslots?.[0]?.project?.uuid;
        const restrictToPrimaryProject = this.invoiceMode !== 'ses';
        const byId = new Map<string, Timeslot>();

        const addSlot = (slot: Timeslot | null | undefined, parent?: Timesheet | null) => {
            if (!slot) {
                return;
            }
            if (restrictToPrimaryProject && projectId && slot.project?.uuid !== projectId) {
                return;
            }
            if (parent && !slot.timesheet) {
                slot.timesheet = parent;
            }
            const key = slot.uuid || `${slot.project?.uuid || ''}:${slot.createdOn || ''}:${slot.minutes || 0}:${byId.size}`;
            if (!byId.has(key)) {
                byId.set(key, slot);
            }
        };

        // BY_TIME receives these exact project timeslots from the invoice list.
        // BY_SES must reuse this same pool so logged hours stay consistent.
        (this.timeslots || []).forEach((slot) => addSlot(slot, slot?.timesheet || this.findTimesheetForSlot(slot)));

        // Extra months fetched for a wider SES period are still legitimate
        // invoice timeslots; include them only after the dialog explicitly loads
        // those surrounding months.
        (this.previousTimesheetGroups || []).forEach((group) => {
            (group.timeslots || []).forEach((slot) => addSlot(slot, slot?.timesheet || this.findTimesheetForSlot(slot)));
        });

        (this.previousTimesheets || []).forEach((timesheet) => {
            (timesheet.timeslots || []).forEach((slot) => addSlot(slot, timesheet));
        });

        return Array.from(byId.values());
    }

    private findTimesheetForSlot(timeslot: Timeslot): Timesheet | null {
        return this.getAllTimesheets().find((ts) => ts.timeslots?.some((slot) => slot.uuid === timeslot.uuid)) || null;
    }

    setDefaultProjectManagementFee(): void {
        const projectType = this.project?.projectType?.toString().toUpperCase();
        // Disable for CONSULTANCY, enable for all others
        if (projectType === 'CONSULTANCY' || projectType === '2') {
            this.addProjectManagementFee = false;
        } else {
            // Enable for CUSTOM, WEBSITE, and all others
            this.addProjectManagementFee = true;
        }
    }

    isProjectManagementFeeDisabled(): boolean {
        const projectType = this.project?.projectType?.toString().toUpperCase();
        // Disable checkbox for CONSULTANCY type
        return projectType === 'CONSULTANCY' || projectType === '2';
    }

    getPartnerName(): string {
        if (this.project && this.project.partnerName && this.project.partnerName !== '') {
            return this.project.partnerName;
        } else {
            return 'NO PARTNERNAME SET';
        }
    }

    getEmployeeByTimeslot(timeslot: Timeslot): Employee {
        const timesheet = this.findTimesheetForSlot(timeslot);
        this.employee = timesheet?.employee;
        return this.employee;
    }

    getDescriptionForTimeslot(timeslot: Timeslot): string {
        const baseDescription = (timeslot as any)._editedDescription
            ? (timeslot as any)._editedDescription
            : (() => {
                  const employee = this.getEmployeeByTimeslot(timeslot);
                  const employeeName = employee ? `${employee.firstName} ${employee.lastName}` : 'Unknown';
                  return `${employeeName} - ${timeslot.project.title}`;
              })();

        const monthLabel = (timeslot as any)._previousMonthLabel;
        return monthLabel ? `${baseDescription} (${monthLabel})` : baseDescription;
    }

    onDescriptionBlur(timeslot: Timeslot): void {
        const newDescription = (timeslot as any)._editedDescription;
        const employee = this.getEmployeeByTimeslot(timeslot);
        const employeeName = employee ? `${employee.firstName} ${employee.lastName}` : 'Unknown';
        const baseOriginalDescription = `${employeeName} - ${timeslot.project.title}`;
        const monthLabel = (timeslot as any)._previousMonthLabel;
        const originalDescription = monthLabel ? `${baseOriginalDescription} (${monthLabel})` : baseOriginalDescription;

        // Only update if description has changed
        if (newDescription === originalDescription || !newDescription) {
            return;
        }

        console.log(`📝 Description changed from "${originalDescription}" to "${newDescription}"`);
        // The description is just stored in _editedDescription and will be used during invoice generation
        this.toastr.info(`Description updated to "${newDescription}"`);
    }

    checkIfAllInvoiceLinesHaveBeenBilled(): boolean {
        let allTimeslotsHaveBeenBilled = true;
        this.timeslots.forEach((slot) => {
            if (slot.billed.valueOf() == false) {
                allTimeslotsHaveBeenBilled = false;
            }
        });

        return allTimeslotsHaveBeenBilled;
    }

    createInvoiceDate(): Date {
        const expirationDate: any = this.timesheets[0].expirationDate;
        return new Date(expirationDate);
    }

    getSubjectWithDateByTimesheet(): string {
        const expirationDate: Date = this.createInvoiceDate();
        const projectName = (this.project?.title || '').toString().trim();

        // Determine project type label
        const rawType = (this.project?.projectType || '').toString().trim().toUpperCase();
        let baseLabel = 'Custom Work';
        if (rawType === 'CONSULTANCY' || rawType === 'CONSULT' || rawType === '2') {
            baseLabel = 'Consultancy';
        } else if (rawType === 'CUSTOM' || rawType === '0') {
            baseLabel = 'Custom Work';
        } else if (rawType) {
            // Fallback: title-case the type string
            const lower = rawType.toLowerCase();
            baseLabel = lower.charAt(0).toUpperCase() + lower.slice(1);
        }

        // Format month-year in Dutch and capitalize first letter
        if (!(expirationDate instanceof Date)) {
            return projectName ? `${baseLabel} DATE UNKNOWN: ${projectName}` : `${baseLabel} DATE UNKNOWN`;
        }
        const nl = expirationDate.toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' });
        const monthYear = nl ? nl.charAt(0).toUpperCase() + nl.slice(1) : '';

        const left = `${baseLabel} ${monthYear}`.trim();
        return projectName ? `${left}: ${projectName}` : left;
    }

    public getProjectHoursPerDay(): number {
        const normalizeHours = (value: any): number | null => {
            if (value === null || value === undefined || value === '') {
                return null;
            }
            const parsed = Number(String(value).replace(',', '.'));
            return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
        };

        const candidates = [
            (this.project as any)?.hoursPerDay,
            (this.project as any)?.hours_per_day,
            this.timeslots && this.timeslots.length > 0 ? (this.timeslots[0] as any)?.project?.hoursPerDay : null,
            this.timeslots && this.timeslots.length > 0 ? (this.timeslots[0] as any)?.project?.hours_per_day : null,
        ];

        for (const candidate of candidates) {
            const hours = normalizeHours(candidate);
            if (hours !== null) {
                return hours;
            }
        }

        return 8;
    }

    private getRateConversionHoursPerDay(): number {
        return 8;
    }

    calculateWorkedHoursForTimeslot(timeslot: Timeslot): number {
        const workedHours = timeslot.minutes / 60;
        const hoursPerDay = this.getProjectHoursPerDay();
        const quantity = this.project?.invoiceByDay ? workedHours / hoursPerDay : workedHours;
        return Number(quantity.toFixed(2));
    }

    calculatePriceForTimeslot(timeslot: Timeslot): number {
        console.log('💵 calculatePriceForTimeslot called for:', timeslot.project.title);

        // Find the timesheet that contains this timeslot to get the employee
        const timesheet = this.findTimesheetForSlot(timeslot);
        if (!timesheet || !timesheet.employee) {
            console.log('❌ No timesheet or employee found');
            return 0;
        }

        const workedHours = this.calculateWorkedHoursForTimeslot(timeslot);
        const pricePerHour = this.getPricePerHourForTimeslot(timeslot);
        console.log('⏱️  Worked hours:', workedHours);
        console.log('💰 Price per hour:', pricePerHour);

        const lineTotal = Number((workedHours * pricePerHour).toFixed(2));
        console.log('✅ Final price:', lineTotal);

        return lineTotal;
    }

    calculateEditablePriceForTimeslot(timeslot: Timeslot): number {
        // Use edited values if available, otherwise use defaults
        const hours = (timeslot as any)?._editedHours ?? this.calculateWorkedHoursForTimeslot(timeslot);
        const rate = (timeslot as any)?._editedRate ?? this.getPricePerHourForTimeslot(timeslot);
        return Number((hours * rate).toFixed(2));
    }

    calculateTotalBeforeVat(): number {
        if (this.invoiceMode === 'account') {
            // Sum of selected account lines (hours × effectiveRate)
            return (this.accountIssueLines || [])
                .filter((l) => l.selected && l.totalHours > 0)
                .reduce((sum, l) => sum + (l.totalHours || 0) * (l.effectiveRate || 0), 0);
        }
        if (this.invoiceMode === 'ses') {
            return this.getSelectedSesAmount();
        }
        // Time mode: sum selected timeslots
        return (this.selectedTimeslots || []).reduce((sum, ts) => {
            const hours = (ts as any)?._editedHours ?? this.calculateWorkedHoursForTimeslot(ts);
            const rate = ((ts as any)?._editedRate ?? this.getPricePerHourForTimeslot(ts)) || 1;
            return sum + hours * rate;
        }, 0);
    }

    calculateProjectManagementFeeBeforeVat(): number {
        if (!this.addProjectManagementFee) return 0;
        let totalBeforeVat = 0;
        if (this.invoiceMode === 'account') {
            totalBeforeVat = this.accountIssueLines
                .filter((l) => l.selected && l.totalHours > 0)
                .reduce((sum, l) => sum + l.totalHours * (l.effectiveRate || 0), 0);
        } else if (this.invoiceMode === 'ses') {
            totalBeforeVat = this.getSelectedSesAmount();
        } else {
            totalBeforeVat = this.selectedTimeslots.reduce((sum, ts) => {
                const hours = (ts as any)?._editedHours ?? this.calculateWorkedHoursForTimeslot(ts);
                const rate = ((ts as any)?._editedRate ?? this.getPricePerHourForTimeslot(ts)) || 1;
                return sum + hours * rate;
            }, 0);
        }
        return Number((totalBeforeVat * (this.projectManagementPercentage / 100)).toFixed(2));
    }

    calculateProjectManagementFeeWithVat(): number {
        return Number((this.calculateProjectManagementFeeBeforeVat() * 1).toFixed(2));
    }

    getPricePerHourForTimeslot(timeslot: Timeslot): number {
        const best = this.getBestDefaultLinkForTimeslot(timeslot);
        if (best) {
            const rateHoursPerDay = this.getRateConversionHoursPerDay();
            return Number(this.project?.invoiceByDay ? Number(best.pricePerHour || 0) * rateHoursPerDay : Number(best.pricePerHour || 0));
        }
        return 0;
    }

    private getBestDefaultLinkForTimeslot(timeslot: Timeslot): any | null {
        const timesheet = this.findTimesheetForSlot(timeslot);
        if (!timesheet?.employee?.uuid) {
            return null;
        }
        const employeeId = timesheet.employee.uuid;
        const refDate = this.invoiceDate ? moment(this.invoiceDate).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD');
        const ref = moment(refDate, 'YYYY-MM-DD');

        if (this.uniqueProjects && Array.isArray(this.uniqueProjects)) {
            const fullProject = this.uniqueProjects.find((p) => p.uuid === timeslot.project.uuid);
            if (fullProject?.defaultLinkedEmployees?.length > 0) {
                const links = fullProject.defaultLinkedEmployees.filter((l) => l.employeeId === employeeId);
                const best = this.getBestPriceLinkByDate(links, ref);
                if (best) return best;
            }
        }

        if (this.project?.uuid === timeslot.project.uuid && this.project?.defaultLinkedEmployees?.length > 0) {
            const links = this.project.defaultLinkedEmployees.filter((l) => l.employeeId === employeeId);
            const best = this.getBestPriceLinkByDate(links, ref);
            if (best) return best;
        }

        if (timeslot.project?.defaultLinkedEmployees?.length > 0) {
            const links = timeslot.project.defaultLinkedEmployees.filter((l) => l.employeeId === employeeId);
            const best = this.getBestPriceLinkByDate(links, ref);
            if (best) return best;
        }

        return null;
    }

    private getBestPriceLinkByDate(links: any[], ref: any): any | null {
        try {
            if (!Array.isArray(links) || links.length === 0) return null;
            const parsed = links.map((l) => ({
                raw: l,
                effectiveFrom: l.effectiveFrom ? moment(l.effectiveFrom, 'YYYY-MM-DD') : null,
                effectiveTo: l.effectiveTo ? moment(l.effectiveTo, 'YYYY-MM-DD') : null,
            }));
            const eligible = parsed.filter((l) => {
                const fromOk = !l.effectiveFrom || l.effectiveFrom.isSameOrBefore(ref);
                const toOk = !l.effectiveTo || l.effectiveTo.isSameOrAfter(ref);
                return fromOk && toOk;
            });
            if (eligible.length === 0) {
                const sortedFuture = parsed.filter((l) => l.effectiveFrom).sort((a, b) => a.effectiveFrom!.valueOf() - b.effectiveFrom!.valueOf());
                return sortedFuture.length ? sortedFuture[0].raw : parsed[0].raw;
            }
            const sorted = eligible.sort((a, b) => {
                const av = a.effectiveFrom ? a.effectiveFrom.valueOf() : -Infinity;
                const bv = b.effectiveFrom ? b.effectiveFrom.valueOf() : -Infinity;
                return bv - av; // descending
            });
            return sorted[0]?.raw || null;
        } catch {
            return null;
        }
    }

    onPriceBlur(timeslot: Timeslot): void {
        const newPrice = (timeslot as any)._editedRate;
        const originalPrice = this.getPricePerHourForTimeslot(timeslot);

        // Only update if price has changed
        if (newPrice === originalPrice || !newPrice) {
            return;
        }

        // Find the employee for this timeslot
        const timesheet = this.findTimesheetForSlot(timeslot);

        if (!timesheet || !timesheet.employee || !this.project) {
            return;
        }

        const employeeId = timesheet.employee.uuid;
        const rateHoursPerDay = this.getRateConversionHoursPerDay();
        const storeRate = this.project?.invoiceByDay ? Number(newPrice) / rateHoursPerDay : Number(newPrice);
        console.log(`💾 Saving price ${newPrice} (store hourly=${storeRate}) for employee ${employeeId} on project ${this.project.uuid}`);

        // Check if this employee is already linked to the project
        const existingLink = this.project.defaultLinkedEmployees?.find((l) => l.employeeId === employeeId);

        if (existingLink) {
            // Delete the old link, then create a new one with updated price
            this.subscriptions.add(
                this.projectService
                    .deleteDefaultEmployee(existingLink.uuid)
                    .pipe(
                        tap(() => {
                            // After delete succeeds, create new link with new price
                            const newDTO: DefaultProjectEmployeeLinkDTO = {
                                employeeIds: [employeeId],
                                projectId: this.project.uuid,
                                pricePerHour: storeRate,
                            };
                            this.store.dispatch(new AddDefaultEmployees(newDTO));
                        }),
                        catchError((deleteError) => {
                            console.error('Failed to delete old price:', deleteError);
                            this.toastr.error('Failed to update price');
                            return of(null);
                        }),
                    )
                    .subscribe(() => {
                        // Success - price updated
                        const unit = this.project?.invoiceByDay ? 'day' : 'hour';
                        this.toastr.success(`Price updated to €${newPrice}/${unit}`);
                    }),
            );
        } else {
            // No existing link, just create a new one
            const newDTO: DefaultProjectEmployeeLinkDTO = {
                employeeIds: [employeeId],
                projectId: this.project.uuid,
                pricePerHour: storeRate,
            };
            this.subscriptions.add(
                this.store.dispatch(new AddDefaultEmployees(newDTO)).subscribe(
                    () => {
                        const unit = this.project?.invoiceByDay ? 'day' : 'hour';
                        this.toastr.success(`Price set to €${newPrice}/${unit}`);
                    },
                    (error) => {
                        console.error('Failed to save price:', error);
                        this.toastr.error('Failed to save price');
                    },
                ),
            );
        }
    }

    private loadPreviousUnbilledTimeslots(): void {
        if (!this.timeslots?.length) {
            return;
        }

        const projectId = this.timeslots[0]?.project?.uuid;
        if (!projectId) {
            return;
        }

        const baseDateRef = this.worklogsFrom || this.invoiceDate || moment().format('YYYY-MM-DD');
        const baseDate = moment(baseDateRef, 'YYYY-MM-DD').startOf('month');
        const monthsBefore = this.normalizeAdditionalMonths(this.additionalMonthsBefore);
        const monthsAfter = this.normalizeAdditionalMonths(this.additionalMonthsAfter);
        this.additionalMonthsBefore = monthsBefore;
        this.additionalMonthsAfter = monthsAfter;

        if (monthsBefore === 0 && monthsAfter === 0) {
            const visibleTimeslotIds = new Set((this.timeslots || []).map((slot) => slot?.uuid).filter((id) => !!id));
            this.selectedTimeslots = (this.selectedTimeslots || []).filter((slot) => {
                const id = slot?.uuid;
                return !!id && visibleTimeslotIds.has(id);
            });
            this.previousTimesheetGroups = [];
            this.previousTimesheets = [];
            this.isLoadingPreviousTimeslots = false;
            this.updateCreateDisabled();
            return;
        }

        const requests: Array<Observable<{ label: string; timesheets: Timesheet[] }>> = [];

        for (let i = 1; i <= monthsBefore; i++) {
            const targetMonth = baseDate.clone().subtract(i, 'month');
            const label = targetMonth.format('MMMM YYYY');
            requests.push(
                this.timesheetsService.fetchTimesheetsByProjectIdAndYearAndMonth(projectId, targetMonth.year(), targetMonth.month() + 1).pipe(
                    map((timesheets) => ({ label, timesheets: timesheets || [] })),
                    catchError((error) => {
                        this.logger.warn(`InvoiceDialog: Failed to fetch timesheets for ${label}`, error);
                        return of({ label, timesheets: [] as Timesheet[] });
                    }),
                ),
            );
        }

        for (let i = 1; i <= monthsAfter; i++) {
            const targetMonth = baseDate.clone().add(i, 'month');
            const label = targetMonth.format('MMMM YYYY');
            requests.push(
                this.timesheetsService.fetchTimesheetsByProjectIdAndYearAndMonth(projectId, targetMonth.year(), targetMonth.month() + 1).pipe(
                    map((timesheets) => ({ label, timesheets: timesheets || [] })),
                    catchError((error) => {
                        this.logger.warn(`InvoiceDialog: Failed to fetch timesheets for ${label}`, error);
                        return of({ label, timesheets: [] as Timesheet[] });
                    }),
                ),
            );
        }

        this.isLoadingPreviousTimeslots = true;

        this.subscriptions.add(
            this.track(forkJoin(requests)).subscribe((results) => {
                const currentTimeslotIds = new Set((this.timeslots || []).map((ts) => ts.uuid));
                const grouped: Array<{ label: string; timeslots: Timeslot[] }> = [];
                const collectedTimesheets: Timesheet[] = [];

                results.forEach((result) => {
                    collectedTimesheets.push(...result.timesheets);
                    const unbilledSlots = result.timesheets.reduce((acc: Timeslot[], ts) => {
                        const slots = (ts.timeslots || []).filter((slot) => slot && !slot.billed && slot.project?.uuid === projectId);
                        if (slots.length > 0) {
                            acc.push(...slots);
                        }
                        return acc;
                    }, [] as Timeslot[]);
                    const filteredSlots = unbilledSlots.filter((slot) => slot.uuid && !currentTimeslotIds.has(slot.uuid));
                    if (filteredSlots.length > 0) {
                        filteredSlots.forEach((slot) => {
                            (slot as any)._previousMonthLabel = result.label;
                            const parent = result.timesheets.find((ts) => ts.timeslots?.some((s) => s.uuid === slot.uuid));
                            if (parent) {
                                slot.timesheet = parent;
                            }
                        });
                        grouped.push({ label: result.label, timeslots: filteredSlots });
                    }
                });

                this.previousTimesheetGroups = grouped;
                this.previousTimesheets = collectedTimesheets;
                this.isLoadingPreviousTimeslots = false;
            }),
        );
    }

    getPreviousTimeslotCount(): number {
        return this.previousTimesheetGroups.reduce((sum, group) => sum + group.timeslots.length, 0);
    }

    toggleTimeslotSelection(checked: boolean, timeslot: Timeslot): void {
        try {
            console.debug('toggleTimeslotSelection called', { checked, timeslotUuid: timeslot?.uuid });
            if (checked) {
                // Ensure invoice mode is 'time' when selecting a timeslot
                this.invoiceMode = 'time';
                // Avoid duplicate entries (compare by uuid)
                const exists = (this.selectedTimeslots || []).some((ts) => ts?.uuid === timeslot?.uuid);
                if (!exists) {
                    this.selectedTimeslots.push(timeslot);
                }
            } else {
                // Remove by uuid to avoid reference-equality issues
                this.selectedTimeslots = (this.selectedTimeslots || []).filter((ts) => ts?.uuid !== timeslot?.uuid);
                this.removeAttachment(timeslot?.uuid);
            }
            console.debug('toggleTimeslotSelection result', { invoiceMode: this.invoiceMode, selectedCount: (this.selectedTimeslots || []).length });
        } catch (e) {
            console.error('toggleTimeslotSelection error', e);
            // Fallback: original behavior
            if (checked) {
                this.selectedTimeslots.push(timeslot);
            } else {
                const index = this.selectedTimeslots.indexOf(timeslot);
                if (index !== -1) {
                    this.selectedTimeslots.splice(index, 1);
                }
            }
        }
        // Re-evaluate disabled state after selection/mode change
        this.updateCreateDisabled();
        // Ensure Angular runs change detection so template reflects the updated selection/state
        try {
            this.cdr.detectChanges();
        } catch {
            // ignore errors if change detection cannot be run here
        }
    }

    toggleAttachmentSelection(checked: boolean, timeslot: Timeslot): void {
        if (!timeslot?.pdfUrl) {
            return;
        }
        if (checked) {
            const fileName = this.extractFileName(timeslot.pdfUrl);
            // Avoid duplicates
            const exists = this.selectedAttachments.some((a) => a.timeslotUuid === timeslot.uuid);
            if (!exists) {
                this.selectedAttachments.push({
                    timeslotUuid: timeslot.uuid,
                    pdfUrl: timeslot.pdfUrl,
                    fileName,
                });
                // Load existing validation metadata from DB (saved at upload)
                this.loadExistingValidation(timeslot);
            }
        } else {
            this.selectedAttachments = this.selectedAttachments.filter((a) => a.timeslotUuid !== timeslot.uuid);
            delete this.attachmentValidationResults[timeslot.uuid];
            delete this.isValidatingAttachment[timeslot.uuid];
        }
    }

    extractFileName(pdfUrl: string): string {
        if (!pdfUrl) return 'attachment.pdf';
        const parts = pdfUrl.split('/');
        return parts[parts.length - 1] || 'attachment.pdf';
    }

    removeAttachment(timeslotUuid: string): void {
        this.selectedAttachments = this.selectedAttachments.filter((a) => a.timeslotUuid !== timeslotUuid);
        this.logger.debug('Removed attachment for timeslot:', timeslotUuid);
    }

    previewAttachment(pdfUrl: string): void {
        if (pdfUrl) {
            openExternalUrl(pdfUrl);
            this.logger.debug('Opening PDF preview:', pdfUrl);
        }
    }

    private findTimeslotByUuid(uuid: string): Timeslot | null {
        if (!uuid) return null;
        // Prefer currently selected timeslots first (most up-to-date reference)
        const selected = (this.selectedTimeslots || []).find((ts) => ts.uuid === uuid);
        if (selected) return selected;
        // Then search current project timeslots
        const current = (this.timeslots || []).find((ts) => ts.uuid === uuid);
        if (current) return current;
        // Finally, search previous grouped timesheets
        for (const group of this.previousTimesheetGroups || []) {
            const found = (group.timeslots || []).find((ts) => ts.uuid === uuid);
            if (found) return found;
        }
        return null;
    }

    hasAnyPdf(): boolean {
        return (this.selectedTimeslots || []).some((ts) => !!ts.pdfUrl);
    }

    getAllPdfEntries(): Array<{ timeslotUuid: string; pdfUrl: string; fileName: string; monthLabel?: string }> {
        const entries: Array<{ timeslotUuid: string; pdfUrl: string; fileName: string; monthLabel?: string }> = [];
        (this.selectedTimeslots || []).forEach((ts) => {
            if (ts?.pdfUrl) {
                const monthLabel = (ts as any)._previousMonthLabel;
                entries.push({
                    timeslotUuid: ts.uuid,
                    pdfUrl: ts.pdfUrl,
                    fileName: this.extractFileName(ts.pdfUrl),
                    monthLabel,
                });
            }
        });
        return entries;
    }

    isAttachmentSelected(timeslotUuid: string): boolean {
        return this.selectedAttachments.some((a) => a.timeslotUuid === timeslotUuid);
    }

    private loadExistingValidation(timeslot: Timeslot): void {
        try {
            // Prefer using embedded timeslot fields if present
            const embedded = {
                consultantName: (timeslot as any).validatedConsultantName,
                timesheetType: (timeslot as any).validatedTimesheetType,
                periodISO: (timeslot as any).validatedPeriodISO,
                signed: (timeslot as any).validatedSigned,
                hours: (timeslot as any).validatedHours,
            };
            const hasEmbedded = !!(
                embedded.consultantName ||
                embedded.timesheetType ||
                embedded.periodISO ||
                embedded.signed === true ||
                embedded.signed === false ||
                typeof embedded.hours === 'number'
            );
            const assignResult = (v: { consultantName?: string; timesheetType?: string; periodISO?: string; signed?: boolean; hours?: number } | null) => {
                const parsedHours = typeof v?.hours === 'number' ? v.hours : undefined;
                const pdfMonthISO = v?.periodISO || undefined;
                const type = (v?.timesheetType || '').toString().trim();
                const signed = v?.signed;
                const employeePresent = !!v?.consultantName;
                const expectedMonthISO = this.invoiceDate ? moment(this.invoiceDate).startOf('month').format('YYYY-MM-DD') : undefined;
                this.attachmentValidationResults[timeslot.uuid] = {
                    ok: true,
                    issues: [],
                    type,
                    details: { parsedHours, pdfMonthISO, expectedMonthISO, signed, employeePresent },
                };
            };
            if (hasEmbedded) {
                assignResult(embedded);
                return;
            }
            // Fallback: fetch from backend token-aware public/protected endpoint
            this.subscriptions.add(
                this.track(this.timesheetsService.fetchTimeslotValidation(timeslot.uuid)).subscribe({
                    next: (v) => assignResult(v || null),
                    error: () => {
                        // No saved validation available
                        this.attachmentValidationResults[timeslot.uuid] = { ok: false, issues: ['Geen opgeslagen validatie gevonden.'] };
                    },
                }),
            );
        } catch {
            this.attachmentValidationResults[timeslot.uuid] = { ok: false, issues: ['Kon validatie niet laden.'] };
        }
    }

    private preloadExistingValidationForAllPdfs(): void {
        try {
            const allWithPdf = (this.timeslots || []).filter((ts) => !!ts?.pdfUrl);
            allWithPdf.forEach((ts) => this.loadExistingValidation(ts));
        } catch {
            // ignore
        }
    }

    private runAttachmentValidation(timeslot: Timeslot): void {
        try {
            const pdfUrl = timeslot.pdfUrl || '';
            if (!pdfUrl) return;
            const emp = this.getEmployeeByTimeslot(timeslot);
            const employeeName = emp ? `${emp.firstName} ${emp.lastName}` : '';
            // Expected hours for AI: if invoice-by-day, use (days × hoursPerDay), otherwise raw worked hours
            const workedHoursRaw = (timeslot.minutes + (timeslot.leaveMinutes || 0)) / 60;
            const hoursPerDay = this.getProjectHoursPerDay();
            const daysWorked = workedHoursRaw / hoursPerDay;
            const expectedHours = this.project?.invoiceByDay ? Number((daysWorked * hoursPerDay).toFixed(2)) : Number(workedHoursRaw.toFixed(2));
            const monthLabel = (timeslot as any)._previousMonthLabel as string | undefined;
            // Uploaded month: derive from invoice date
            const uploadedMonthISO = this.invoiceDate
                ? moment(this.invoiceDate).startOf('month').format('YYYY-MM-DD')
                : moment().startOf('month').format('YYYY-MM-DD');
            const timesheetTypeHint = (this.project?.timesheetType || '').toString().trim();
            this.logger.info('[INVOICE] Starting AI validation for attachment', {
                timeslotUuid: timeslot.uuid,
                pdfUrl,
                employeeName,
                expectedHours,
                monthLabel,
                uploadedMonthISO,
                timesheetTypeHint,
            });
            this.isValidatingAttachment[timeslot.uuid] = true;
            this.tsAiValidator
                .validate(pdfUrl, { employeeName, expectedHours, monthLabel, uploadedMonthISO, timesheetTypeHint })
                .then((result) => {
                    this.logger.info('[INVOICE] AI validation result for', timeslot.uuid, result);
                    this.attachmentValidationResults[timeslot.uuid] = {
                        ok: result.ok,
                        issues: result.issues || [],
                        type: result.type,
                        details: result.details || {},
                    };
                    // Persist compact validation back to backend
                    const payload = {
                        consultantName: employeeName || undefined,
                        timesheetType: (result.type || timesheetTypeHint || '').trim() || undefined,
                        periodISO: (result.details?.pdfMonthISO as any) || uploadedMonthISO || undefined,
                        signed: result.details?.signed,
                        hours: typeof result.details?.parsedHours === 'number' ? result.details.parsedHours : expectedHours,
                    };
                    this.timesheetsService.saveTimeslotValidation(timeslot.uuid, payload).subscribe({
                        next: () => {},
                        error: () => {},
                    });
                })
                .catch((err) => {
                    this.logger.warn('AI validation failed', err);
                    this.attachmentValidationResults[timeslot.uuid] = { ok: false, issues: ['Validatie mislukt door een fout.'] };
                })
                .finally(() => {
                    this.logger.info('[INVOICE] AI validation finished for', timeslot.uuid);
                    this.isValidatingAttachment[timeslot.uuid] = false;
                });
        } catch (e) {
            this.logger.warn('runAttachmentValidation error', e);
        }
    }

    hasValidationIssues(): boolean {
        try {
            return Object.values(this.attachmentValidationResults || {}).some((r) => !r.ok && (r.issues || []).length > 0);
        } catch {
            return false;
        }
    }

    getValidationSummary(): Array<{ fileName: string; issues: string[]; loading?: boolean }> {
        try {
            const byUuid: Record<string, { fileName: string; issues: string[]; loading?: boolean }> = {};
            (this.selectedAttachments || []).forEach((att) => {
                const res = this.attachmentValidationResults[att.timeslotUuid];
                byUuid[att.timeslotUuid] = {
                    fileName: att.fileName,
                    issues: res ? res.issues || [] : [],
                    loading: !!this.isValidatingAttachment[att.timeslotUuid],
                };
            });
            return Object.values(byUuid);
        } catch {
            return [];
        }
    }

    isAnyValidationInProgress(): boolean {
        try {
            const entries = this.getAllPdfEntries();
            return entries.some((e) => !!this.isValidatingAttachment[e.timeslotUuid]);
        } catch {
            return false;
        }
    }

    getValidationChecks(): Array<{ fileName: string; statuses: Array<{ label: string; value: 'ok' | 'fout' | 'onbekend'; display: string }> }> {
        try {
            const results: Array<{ fileName: string; statuses: Array<{ label: string; value: 'ok' | 'fout' | 'onbekend'; display: string }> }> = [];
            const entries = this.getAllPdfEntries();
            entries.forEach((att) => {
                const timeslot = this.findTimeslotByUuid(att.timeslotUuid);
                // Ensure we have loaded existing validation details
                if (!this.attachmentValidationResults[att.timeslotUuid] && timeslot) {
                    this.loadExistingValidation(timeslot);
                }
                const res = this.attachmentValidationResults[att.timeslotUuid];
                const statuses: Array<{ label: string; value: 'ok' | 'fout' | 'onbekend'; display: string }> = [];

                // Hours
                const parsedHours = res?.details?.parsedHours;
                const totalMinutes = timeslot ? timeslot.minutes + (timeslot.leaveMinutes || 0) : 0;
                const expectedHours = timeslot
                    ? this.project?.invoiceByDay
                        ? Number(((totalMinutes / 60 / this.getProjectHoursPerDay()) * this.getProjectHoursPerDay()).toFixed(2))
                        : Number((totalMinutes / 60).toFixed(2))
                    : undefined;
                let hoursVal: 'ok' | 'fout' | 'onbekend' = 'onbekend';
                if (typeof res?.details?.hoursOk === 'boolean') {
                    hoursVal = res.details.hoursOk ? 'ok' : 'fout';
                } else if (typeof parsedHours === 'number' && typeof expectedHours === 'number') {
                    hoursVal = Math.abs(parsedHours - expectedHours) < 0.51 ? 'ok' : 'fout';
                }
                const hoursDisplay =
                    `${typeof parsedHours === 'number' ? parsedHours.toFixed(0) : '?'}` +
                    ` van ${typeof expectedHours === 'number' ? expectedHours.toFixed(0) : '?'}`;
                statuses.push({ label: 'Uren', value: hoursVal, display: hoursDisplay });

                // Month
                const pdfMonthISO = res?.details?.pdfMonthISO as string | undefined;
                const expectedMonthISO = res?.details?.expectedMonthISO as string | undefined;
                let monthVal: 'ok' | 'fout' | 'onbekend' = 'onbekend';
                if (typeof res?.details?.monthOk === 'boolean') {
                    monthVal = res.details.monthOk ? 'ok' : 'fout';
                } else if (pdfMonthISO && (expectedMonthISO || this.invoiceDate)) {
                    try {
                        const refDate = expectedMonthISO ? expectedMonthISO : this.invoiceDate;
                        const up = (refDate ? moment(refDate).format('MMMM YYYY') : '').toLowerCase();
                        const isoYear = String(pdfMonthISO).slice(0, 4);
                        const isoMonth = String(pdfMonthISO).slice(5, 7);
                        const monthNames = [
                            'january',
                            'february',
                            'march',
                            'april',
                            'may',
                            'june',
                            'july',
                            'august',
                            'september',
                            'october',
                            'november',
                            'december',
                        ];
                        const mName = monthNames[Number(isoMonth) - 1 || 0];
                        monthVal = up.includes(mName) && up.includes(isoYear) ? 'ok' : 'fout';
                    } catch {
                        monthVal = 'onbekend';
                    }
                }
                const monthDisplay = pdfMonthISO
                    ? moment(pdfMonthISO).format('MMMM YYYY')
                    : expectedMonthISO
                    ? moment(expectedMonthISO).format('MMMM YYYY')
                    : this.invoiceDate
                    ? moment(this.invoiceDate).format('MMMM YYYY')
                    : 'onbekend';
                statuses.push({ label: 'Maand', value: monthVal, display: monthDisplay });

                // Name present
                let nameVal: 'ok' | 'fout' | 'onbekend' = 'onbekend';
                if (typeof res?.details?.employeePresent === 'boolean') {
                    nameVal = res.details.employeePresent ? 'ok' : 'fout';
                } else {
                    const hasNameIssue = (res?.issues || []).some((s: string) => s.toLowerCase().includes('naam') && s.toLowerCase().includes('niet'));
                    nameVal = hasNameIssue ? 'fout' : 'onbekend';
                }
                const emp = timeslot ? this.getEmployeeByTimeslot(timeslot) : null;
                const nameDisplay = emp ? `${emp.firstName} ${emp.lastName}` : 'onbekend';
                statuses.push({ label: 'Naam', value: nameVal, display: nameDisplay });

                // Signature
                let signVal: 'ok' | 'fout' | 'onbekend' = 'onbekend';
                if (typeof res?.details?.signed === 'boolean') {
                    signVal = res.details.signed ? 'ok' : 'fout';
                } else {
                    const hasSignIssue = (res?.issues || []).some((s: string) => s.toLowerCase().includes('niet ondertekend'));
                    signVal = hasSignIssue ? 'fout' : 'onbekend';
                }
                const signDisplay = signVal === 'ok' ? 'aanwezig' : signVal === 'fout' ? 'ontbreekt' : 'onbekend';
                statuses.push({ label: 'Handtekening', value: signVal, display: signDisplay });

                // Type match
                const detectedType = (res?.type || '').toString().trim();
                const hint = (this.project?.timesheetType || '').toString().trim();
                let typeVal: 'ok' | 'fout' | 'onbekend' = 'onbekend';
                if (detectedType) {
                    typeVal = hint && detectedType.toLowerCase() === hint.toLowerCase() ? 'ok' : 'fout';
                }
                const typeDisplay = detectedType || hint || 'onbekend';
                statuses.push({ label: 'Type', value: typeVal, display: typeDisplay });

                results.push({ fileName: att.fileName, statuses });
            });
            return results;
        } catch {
            return [];
        }
    }

    onSupplierSelected(event: any): void {
        const selectedName = event.target.value;
        console.log('🔄 Supplier selected, name:', selectedName);

        // Find the supplier by name and store its number
        const supplier = this.comanageSuppliers.find((c) => c.name === selectedName);
        if (supplier) {
            this.selectedComanageSupplierId = supplier.number;
            console.log('✅ Supplier found, number:', supplier.number);

            // Save the supplier selection for this project
            this.saveSupplierForProject(supplier.number);
        } else {
            this.selectedComanageSupplierId = null;
            this.saveSupplierForProject(null);
            console.log('⚠️ Supplier not found');
        }
        this.updateCreateDisabled();
    }

    isTempoAccountSelected(accountId: string): boolean {
        return (this.selectedTempoAccountIds || []).includes(accountId);
    }

    onTempoAccountToggled(accountId: string, checked: boolean): void {
        const next = new Set(this.selectedTempoAccountIds || []);
        if (checked) {
            next.add(accountId);
        } else {
            next.delete(accountId);
        }
        this.selectedTempoAccountIds = Array.from(next);
        this.loadWorklogsForSelectedAccounts();
        this.refreshInvoiceFieldsByMode();
        this.jiraReferenceDisplay = this.computeReferenceDisplay();
        this.updateCreateDisabled();
    }

    getSelectedTempoAccounts(): Array<{ id: string; key?: string; name?: string; status?: string }> {
        const selected = new Set(this.selectedTempoAccountIds || []);
        return (this.tempoAccounts || []).filter((a) => selected.has(a.id));
    }

    private computeMonthRange(): { from: string; to: string } {
        const base = this.invoiceDate ? moment(this.invoiceDate) : moment();
        const from = base.clone().startOf('month').format('YYYY-MM-DD');
        const to = base.clone().endOf('month').format('YYYY-MM-DD');
        return { from, to };
    }

    private getWorklogRange(): { from: string; to: string } {
        const from = (this.worklogsFrom || '').trim();
        const to = (this.worklogsTo || '').trim();
        if (from && to) {
            return { from, to };
        }
        return this.computeMonthRange();
    }

    private getPricePerHourForAccountAuthor(jiraAccountId: string): number {
        try {
            if (!jiraAccountId || !this.project) return 0;
            // Find employee UUID by matching jiraEmployeeId on known timesheets (current or previous)
            const allTimesheets = this.getAllTimesheets();
            const tsWithEmployee = allTimesheets.find((t) => (t?.employee?.jiraEmployeeId || '') === jiraAccountId);
            const employeeUuid = tsWithEmployee?.employee?.uuid;
            if (!employeeUuid) return 0;
            // Legacy: derive price from defaultLinkedEmployees for current project
            const link = this.project?.defaultLinkedEmployees?.find((l: any) => l?.employeeId === employeeUuid);
            const rateHour = Number(link?.pricePerHour || 0);
            const rateHoursPerDay = this.getRateConversionHoursPerDay();
            const rate = this.project?.invoiceByDay ? rateHour * rateHoursPerDay : rateHour;
            return rate > 0 ? rate : 0;
        } catch {
            return 0;
        }
    }

    private findProjectAccountPriceForPeriod(accountIdOrKey: string, fromIso: string, toIso: string): number | null {
        try {
            if (!accountIdOrKey || !this.projectAccounts || !this.projectAccounts.length) return null;
            const from = moment(fromIso, 'YYYY-MM-DD');
            const to = moment(toIso, 'YYYY-MM-DD');
            // Normalize search: check jiraAccountId, accountCode, accountName
            const candidates = (this.projectAccounts || []).filter((pa: any) => {
                const idMatch = String(pa?.jiraAccountId || pa?.jiraAccount || pa?.accountId || '').trim() === String(accountIdOrKey).trim();
                const codeMatch = String(pa?.accountCode || pa?.accountKey || pa?.key || '').trim() === String(accountIdOrKey).trim();
                const nameMatch = String(pa?.accountName || '').trim() === String(accountIdOrKey).trim();
                return idMatch || codeMatch || nameMatch;
            });
            if (!candidates.length) return null;
            // Find entries whose effective range intersects with the requested range.
            const applicable = candidates.filter((c: any) => {
                const effFrom = c.effectiveFrom ? moment(c.effectiveFrom, 'YYYY-MM-DD') : null;
                const effTo = c.effectiveTo ? moment(c.effectiveTo, 'YYYY-MM-DD') : null;
                // If no effectiveFrom and no effectiveTo, treat as always applicable
                if (!effFrom && !effTo) return true;
                // Overlap if effFrom <= to AND (no effTo OR effTo >= from)
                const fromOk = !effFrom || effFrom.isSameOrBefore(to);
                const toOk = !effTo || effTo.isSameOrAfter(from);
                return fromOk && toOk;
            });
            if (!applicable.length) return null;
            // Prefer the one with the most recent effectiveFrom <= to
            const sorted = applicable.sort((a: any, b: any) => {
                const aFrom = a.effectiveFrom ? moment(a.effectiveFrom, 'YYYY-MM-DD').valueOf() : -Infinity;
                const bFrom = b.effectiveFrom ? moment(b.effectiveFrom, 'YYYY-MM-DD').valueOf() : -Infinity;
                return bFrom - aFrom;
            });
            const pick = sorted[0];
            const price = Number(pick?.price || pick?.pricePerHour || 0);
            return price > 0 ? price : null;
        } catch {
            return null;
        }
    }

    private loadWorklogsForSelectedAccounts(): void {
        try {
            const selectedAccounts = this.getSelectedTempoAccounts().filter((a) => !!(a.key || '').trim());
            if (selectedAccounts.length === 0) {
                this.accountIssueLines = [];
                this.missingAccountPriceMessages = [];
                return;
            }

            const { from, to } = this.getWorklogRange();
            const rateByAccountId: Record<string, number> = {};
            const missingMessages: string[] = [];

            selectedAccounts.forEach((account) => {
                const key = (account.key || '').trim();
                const rate = this.findProjectAccountPriceForPeriod(account.id || key, from, to) || this.findProjectAccountPriceForPeriod(key, from, to) || 0;
                rateByAccountId[account.id] = Number(rate || 0);
                if (!rateByAccountId[account.id]) {
                    missingMessages.push(`geen prijs gevonden voor deze periode voor account ${this.formatTempoAccountLabel(account)}`);
                }
            });
            this.missingAccountPriceMessages = missingMessages;

            const worklogCalls = selectedAccounts.map((account) =>
                this.track(this.jiraLoginService.getTempoWorklogsByAccount((account.key || '').trim(), from, to, 1000)).pipe(catchError(() => of([] as any[]))),
            );

            this.subscriptions.add(
                this.track(forkJoin(worklogCalls)).subscribe(
                    (worklogResults: any[][]) => {
                        const grouped = new Map<
                            string,
                            {
                                accountId: string;
                                accountKey: string;
                                accountLabel: string;
                                issueId: string;
                                hours: number;
                            }
                        >();
                        const issueIds = new Set<string>();

                        selectedAccounts.forEach((account, index) => {
                            const accountKey = (account.key || '').trim();
                            const accountLabel = this.formatTempoAccountLabel(account);
                            const logs = Array.isArray(worklogResults[index]) ? worklogResults[index] : [];

                            logs.forEach((wl) => {
                                const issueId = String(wl?.issueId || '').trim();
                                if (!issueId) return;

                                issueIds.add(issueId);

                                const billableSeconds = Number(wl?.billableSeconds || 0);
                                const hoursRaw = billableSeconds > 0 ? billableSeconds / 3600 : 0;
                                const hoursPerDay = this.getProjectHoursPerDay();
                                const hours = this.project?.invoiceByDay ? hoursRaw / hoursPerDay : hoursRaw;
                                const mapKey = `${account.id}::${issueId}`;
                                const existing = grouped.get(mapKey) || {
                                    accountId: account.id,
                                    accountKey,
                                    accountLabel,
                                    issueId,
                                    hours: 0,
                                };
                                existing.hours += hours;
                                grouped.set(mapKey, existing);
                            });
                        });

                        if (issueIds.size === 0) {
                            this.accountIssueLines = [];
                            this.updateCreateDisabled();
                            return;
                        }

                        const issueIdList = Array.from(issueIds);
                        const detailCalls = issueIdList.map((id) => this.jiraLoginService.getJiraIssueDetails(id).pipe(catchError(() => of(null))));

                        this.subscriptions.add(
                            this.track(forkJoin(detailCalls)).subscribe((details: any[]) => {
                                const detailByIssueId = new Map<string, any>();
                                (details || []).forEach((detail) => {
                                    const id = String(detail?.id || '').trim();
                                    if (id) {
                                        detailByIssueId.set(id, detail);
                                    }
                                });

                                this.accountIssueLines = Array.from(grouped.values())
                                    .map((entry) => {
                                        const detail = detailByIssueId.get(entry.issueId);
                                        const rate = Number(rateByAccountId[entry.accountId] || 0);
                                        const hours = Number(entry.hours.toFixed(2));
                                        const amount = Number((hours * rate).toFixed(2));

                                        return {
                                            accountId: entry.accountId,
                                            accountKey: entry.accountKey,
                                            accountLabel: entry.accountLabel,
                                            issueId: entry.issueId,
                                            jiraKey: String(detail?.key || '').trim(),
                                            summary: String(detail?.summary || '').trim(),
                                            totalHours: hours,
                                            effectiveRate: rate,
                                            initialEffectiveRate: rate,
                                            totalAmount: amount,
                                            selected: hours > 0,
                                        };
                                    })
                                    .sort((left, right) => {
                                        const accountCompare = (left.accountKey || '').localeCompare(right.accountKey || '');
                                        if (accountCompare !== 0) return accountCompare;
                                        return (left.jiraKey || left.issueId).localeCompare(right.jiraKey || right.issueId);
                                    });

                                this.updateCreateDisabled();
                            }),
                        );
                    },
                    () => {
                        this.accountIssueLines = [];
                        this.updateCreateDisabled();
                    },
                ),
            );
        } catch {
            this.accountIssueLines = [];
            this.missingAccountPriceMessages = [];
            this.updateCreateDisabled();
        }
    }

    onAccountRateInputChanged(line: any, newRate: any): void {
        const rate = Number(newRate) || 0;
        const accountId = String(line?.accountId || '').trim();
        if (!accountId) return;
        this.applyRateToAccountLines(accountId, rate);
    }

    onAccountRateBlur(line?: any): void {
        try {
            if (this.invoiceMode !== 'account') return;
            const accountId = String(line?.accountId || '').trim();
            if (!accountId) return;

            const currentRate = Number(line?.effectiveRate || 0);
            const initialRate = Number(line?.initialEffectiveRate || 0);
            const hasChanged = Number.isFinite(currentRate) && Number.isFinite(initialRate) && Math.abs(currentRate - initialRate) > 0.009;

            if (hasChanged && !this.rateChangeNoticeShownByAccountId.has(accountId)) {
                const accountLabel = String(line?.accountKey || line?.accountLabel || accountId).trim();
                this.rateChangeNoticeShownByAccountId.add(accountId);
                this.toastr.info(`Tariefwijziging wordt hier niet opgeslagen. Pas het tarief aan via Project > Accounts > ${accountLabel}.`);
            }
        } catch (e) {
            console.error('onAccountRateBlur error', e);
        }
    }

    private applyRateToAccountLines(accountId: string, rate: number): void {
        const normalizedAccountId = String(accountId || '').trim();
        if (!normalizedAccountId) return;
        (this.accountIssueLines || []).forEach((l) => {
            if (String(l.accountId || '').trim() !== normalizedAccountId) return;
            l.effectiveRate = Number(rate);
            l.totalAmount = Number(((l.totalHours || 0) * (l.effectiveRate || 0)).toFixed(2));
        });
    }

    onAccountHoursChanged(line: any, newHours: any): void {
        const hours = Number(newHours) || 0;
        line.totalHours = hours > 0 ? Number(hours.toFixed(2)) : 0;
        line.totalAmount = Number(((line.totalHours || 0) * (line.effectiveRate || 0)).toFixed(2));
    }

    trackByIssueLine(index: number, line: { issueId: string }): string {
        return `${(line as any).accountId || 'no-account'}::${line.issueId}`;
    }

    onAccountLineSelectionChanged(line: { selected: boolean }, checked: boolean): void {
        line.selected = !!checked;
        this.updateCreateDisabled();
    }

    public updateCreateDisabled(): void {
        console.debug('updateCreateDisabled called (top)', {
            selectedComanageSupplierId: this.selectedComanageSupplierId,
            invoiceMode: this.invoiceMode,
            selectedTimeslotsCount: (this.selectedTimeslots || []).length,
            selectedAttachmentsCount: (this.selectedAttachments || []).length,
        });
        let disabled = false;
        let hasCustomer = !!this.selectedComanageSupplierId;
        // If user typed/preselected supplier name but `selectedComanageSupplierId` not set,
        // try to resolve by exact name (case-insensitive) so Create Invoice is enabled.
        if (!hasCustomer && (this.supplierSearchText || '').toString().trim().length > 0) {
            const typed = (this.supplierSearchText || '').toString().trim().toLowerCase();
            const match = (this.comanageSuppliers || []).find((c: any) => (c?.name || '').toString().trim().toLowerCase() === typed);
            if (match) {
                this.selectedComanageSupplierId = match.number;
                hasCustomer = true;
            }
        }
        if (!hasCustomer) {
            this.isDisabled = true;
            return;
        }
        // If project requires timesheets, enforce presence of a timesheet type and at least one selected PDF attachment
        const requiresTimesheet = !!(this.project && this.project.timesheetRequired);
        const hasTimesheetType = !!((this.project && (this.project.timesheetType || '').trim()) || false);
        if (requiresTimesheet && !hasTimesheetType) {
            this.isDisabled = true;
            return;
        }
        // If project requires a PO number, block invoice creation until one is provided
        if (this.project?.poRequired && !(this.poNumber || '').trim()) {
            this.isDisabled = true;
            return;
        }
        // If project requires a SES number, block invoice creation until one is provided
        if (this.project?.sesRequired && !this.hasSesReference() && !this.selectedSesDoc) {
            this.isDisabled = true;
            return;
        }
        if (requiresTimesheet) {
            // Require a timesheet type when project mandates timesheets.
            // Do NOT require a PDF attachment here; selecting at least one timeslot
            // will be sufficient to enable invoice creation (handled below).
        }
        if (this.invoiceMode === 'time') {
            const hasTimeSelection = (this.selectedTimeslots || []).length > 0;
            disabled = disabled || !hasTimeSelection;
            this.isDisabled = !!disabled;
            console.debug('updateCreateDisabled', {
                invoiceMode: this.invoiceMode,
                hasCustomer,
                requiresTimesheet,
                hasTimesheetType,
                hasAttachmentSelected: (this.selectedAttachments || []).length > 0,
                selectedTimeslotsCount: (this.selectedTimeslots || []).length,
                disabled: this.isDisabled,
            });
            return;
        }
        if (this.invoiceMode === 'ses') {
            const hasSesDocument = !!this.selectedSesDoc;
            const hasSesAmount = this.getSelectedSesAmount() > 0;
            const hasAllocatedHours = this.getSelectedSesLoggedHours() > 0;
            const hoursMatch = this.isSelectedSesHoursMatch();
            disabled = disabled || !hasSesDocument || !hasSesAmount || !hasAllocatedHours || hoursMatch === false;
            this.isDisabled = !!disabled;
            console.debug('updateCreateDisabled (ses)', {
                invoiceMode: this.invoiceMode,
                hasCustomer,
                hasSesDocument,
                hasSesAmount,
                hasAllocatedHours,
                hoursMatch,
                selectedTimeslotsCount: (this.selectedTimeslots || []).length,
                disabled: this.isDisabled,
            });
            return;
        }
        // account mode
        const hasAccountChosen = (this.selectedTempoAccountIds || []).length > 0;
        const hasAccountLineSelected = this.hasSelectedAccountLine();
        disabled = disabled || !(hasAccountChosen && hasAccountLineSelected);
        this.isDisabled = !!disabled;
        console.debug('updateCreateDisabled (account)', {
            invoiceMode: this.invoiceMode,
            hasCustomer,
            hasAccountChosen,
            hasAccountLineSelected,
            selectedAttachmentsCount: (this.selectedAttachments || []).length,
            disabled: this.isDisabled,
        });
    }

    // Template-safe helper: checks if any account line is selected
    public hasSelectedAccountLine(): boolean {
        try {
            return (this.accountIssueLines || []).some((l: any) => !!l.selected);
        } catch {
            return false;
        }
    }

    // Template helper: check if a given timeslot is selected (compare by uuid)
    public isTimeslotSelected(ts: Timeslot | null | undefined): boolean {
        try {
            if (!ts || !ts.uuid) return false;
            const sel = (this.selectedTimeslots || []).some((s) => s?.uuid === ts.uuid);
            // small debug to trace selection lookup mismatches
            // (can be noisy; remove when issue resolved)
            console.debug('isTimeslotSelected', { timeslotUuid: ts.uuid, selected: sel });
            return sel;
        } catch {
            return false;
        }
    }

    generateInvoiceJSON(contactId: number): any {
        const trimmedText = (this.comanageInvoiceText || '').trim();
        const trimmedPoNumber = (this.poNumber || '').trim();
        const paymentTerm = this.project?.paymentTermDays ?? 30;
        const invoice: any = {
            contact: contactId,
            date: this.invoiceDate,
            title: this.subjectWithDate,
            pdf_template: 1,
            // CoManage supports payment term as number of days
            payment_term: paymentTerm,
            due_days: paymentTerm,
        };
        if (trimmedPoNumber.length > 0) {
            invoice.po_number = trimmedPoNumber;
        }
        const trimmedSesNumber = (this.sesNumber || this.getSesDocumentReference(this.selectedSesDoc) || '').trim();
        if (trimmedSesNumber.length > 0) {
            invoice.ses_number = trimmedSesNumber;
        }
        // Prefer native invoice text fields if supported by CoManage
        if (trimmedText.length > 0) {
            invoice.extra_text = trimmedText; // documented in CoManage for additional text
            invoice.text = trimmedText; // fallback for implementations expecting 'text'
        }
        return invoice;
    }

    generateInvoiceItems(): any[] {
        const items = [];
        if (this.invoiceMode === 'account') {
            // Only account-mode aggregated items; ignore timeslots entirely
            this.accountIssueLines
                .filter((l) => l.selected && l.totalHours > 0)
                .forEach((line) => {
                    const descBase =
                        (line as any)._editedDescription && (line as any)._editedDescription.trim().length > 0
                            ? (line as any)._editedDescription
                            : line.jiraKey
                            ? `${line.jiraKey}${line.summary ? ' - ' + line.summary : ''}`
                            : `Issue ${line.issueId}`;
                    const accountPrefix = line.accountKey ? `[${line.accountKey}] ` : '';
                    const pricePerHour = line.effectiveRate || 0;
                    const pricePerHourIncludingVat = Number((pricePerHour * 1.21).toFixed(2));
                    items.push({
                        type: 'regular',
                        content: `${accountPrefix}${descBase}`,
                        unit: this.project?.invoiceByDay ? 'day' : 'hour',
                        quantity: Number(line.totalHours.toFixed(2)),
                        price_inc_vat: pricePerHourIncludingVat,
                        vat_percentage: 21,
                    });
                });
        } else if (this.invoiceMode === 'ses') {
            const sesNumber = (this.sesNumber || this.getSesDocumentReference(this.selectedSesDoc) || '').trim();
            const poNumber = (this.poNumber || this.selectedSesDoc?.extractedPoNumber || '').trim();
            const period = this.getDocumentPeriodLabel(this.selectedSesDoc);

            // Group selected timeslots by project so each project gets its own invoice line
            // with its own configured hourly rate instead of a single averaged rate.
            const projectGroups = new Map<string, Timeslot[]>();
            (this.selectedTimeslots || []).forEach((slot) => {
                const key = slot.project?.uuid || 'unknown';
                if (!projectGroups.has(key)) projectGroups.set(key, []);
                projectGroups.get(key)!.push(slot);
            });

            if (projectGroups.size > 1) {
                // Multi-project: one invoice line per project with the project's own rate
                projectGroups.forEach((slots) => {
                    const firstSlot = slots[0];
                    const projectTitle = (firstSlot.project?.title || '').trim();
                    const projectHours = Number(slots.reduce((sum, s) => sum + this.getSesAllocatedHoursForTimeslot(s), 0).toFixed(2));
                    const rate = this.getPricePerHourForTimeslot(firstSlot) || this.getSelectedSesUnitRate() || 0;
                    const details = [
                        sesNumber ? `SES ${sesNumber}` : 'SES',
                        poNumber ? `PO ${poNumber}` : '',
                        period || '',
                        projectTitle,
                        projectHours > 0 ? `${projectHours}h` : '',
                        rate > 0 ? `€${Number(rate.toFixed(2))}/h` : '',
                    ]
                        .filter((v) => v.length > 0)
                        .join(' - ');
                    const canInvoiceAsHours = projectHours > 0 && rate > 0;
                    items.push({
                        type: 'regular',
                        content: details || `SES invoice - ${projectTitle}`,
                        unit: canInvoiceAsHours ? 'hour' : '',
                        quantity: canInvoiceAsHours ? projectHours : 1,
                        price_inc_vat: canInvoiceAsHours ? Number((rate * 1.21).toFixed(4)) : 0,
                        vat_percentage: 21,
                    });
                });
            } else {
                // Single project: original behavior (use SES document rate)
                const amount = this.getSelectedSesAmount();
                const approvedHours = this.getSelectedSesApprovedHours();
                const unitRate = this.getSelectedSesUnitRate();
                const projectTitles = this.getSelectedSesProjectTitles();
                const details = [
                    sesNumber ? `SES ${sesNumber}` : 'SES',
                    poNumber ? `PO ${poNumber}` : '',
                    period || '',
                    projectTitles.length > 0 ? projectTitles.join(' + ') : '',
                    approvedHours !== null ? `${approvedHours}h` : '',
                    unitRate !== null ? `€${Number(unitRate.toFixed(2))}/h` : '',
                ]
                    .filter((v) => v.length > 0)
                    .join(' - ');
                const canInvoiceAsHours = approvedHours !== null && approvedHours > 0 && unitRate !== null && unitRate > 0;
                items.push({
                    type: 'regular',
                    content: details || 'SES invoice',
                    unit: canInvoiceAsHours ? 'hour' : '',
                    quantity: canInvoiceAsHours ? Number(approvedHours.toFixed(2)) : 1,
                    price_inc_vat: canInvoiceAsHours ? Number((unitRate * 1.21).toFixed(4)) : Number((amount * 1.21).toFixed(2)),
                    vat_percentage: 21,
                });
            }
        } else {
            // Only time-mode items; ignore account lines entirely
            this.selectedTimeslots.forEach((timeslot) => {
                const workedHours = (timeslot as any)?._editedHours ?? this.calculateWorkedHoursForTimeslot(timeslot);
                const pricePerHour = ((timeslot as any)?._editedRate ?? this.getPricePerHourForTimeslot(timeslot)) || 1;
                const description = this.getDescriptionForTimeslot(timeslot);
                // Do NOT append account info in time mode
                const pricePerHourIncludingVat = Number((pricePerHour * 1.21).toFixed(2));
                items.push({
                    type: 'regular',
                    content: description,
                    unit: this.project?.invoiceByDay ? 'day' : 'hour',
                    quantity: workedHours,
                    price_inc_vat: pricePerHourIncludingVat,
                    vat_percentage: 21,
                });
            });
        }

        // Add project management fee if checked - 15% of total invoice amount
        if (this.addProjectManagementFee) {
            // Calculate total of all selected items (before VAT)
            let totalBeforeVat = 0;
            if (this.invoiceMode === 'account') {
                totalBeforeVat = this.accountIssueLines
                    .filter((l) => l.selected && l.totalHours > 0)
                    .reduce((sum, l) => sum + l.totalHours * (l.effectiveRate || 0), 0);
            } else if (this.invoiceMode === 'ses') {
                totalBeforeVat = this.getSelectedSesAmount();
            } else {
                totalBeforeVat = this.selectedTimeslots.reduce((sum, ts) => {
                    const hours = (ts as any)?._editedHours ?? this.calculateWorkedHoursForTimeslot(ts);
                    const rate = ((ts as any)?._editedRate ?? this.getPricePerHourForTimeslot(ts)) || 1;
                    return sum + hours * rate;
                }, 0);
            }

            const percentage = Number(this.projectManagementPercentage || 0);
            const safePercentage = Number.isFinite(percentage) ? percentage : 0;
            const managementFeeBeforeVat = Number((totalBeforeVat * (safePercentage / 100)).toFixed(2));
            const managementFeeIncludingVat = Number((managementFeeBeforeVat * 1.21).toFixed(2));

            items.push({
                type: 'regular',
                content: `projectmanagement - ${safePercentage}%`,
                unit: '',
                quantity: 1,
                price_inc_vat: managementFeeIncludingVat,
                vat_percentage: 21,
            });
        }

        return items;
    }

    closeDialog(): void {
        this.logger.debug('InvoiceDialog: Closing dialog');
        this.subscriptions.unsubscribe();
        // If invoice was created, pass selectedTimeslots + PDF info back so parent can mark them as billed and show links
        if (this.finalizedInvoice) {
            if (this.invoiceMode === 'ses') {
                this.dialogRef.close({
                    invoiceResults: this.finalizedInvoiceResults,
                });
                return;
            }
            this.dialogRef.close({
                timeslots: this.selectedTimeslots,
                pdfUrl: this.finalizedInvoicePdfUrl,
                invoiceNumber: this.finalizedInvoiceNumber,
                invoiceId: this.finalizedInvoiceId,
                invoiceStatus: this.finalizedInvoiceStatus,
                invoiceDate: this.finalizedInvoiceDate,
                comanageLink: this.finalizedInvoiceLink,
            });
        } else {
            this.dialogRef.close();
        }
    }

    sendInvoice(): void {
        this.logger.debug('InvoiceDialog: sendInvoice() called');

        if (this.invoiceMode === 'ses') {
            if (!this.selectedSesDoc) {
                this.toastr.error('Selecteer eerst een SES-document.');
                return;
            }
            if (this.getSelectedSesAmount() <= 0) {
                this.toastr.error('Het geselecteerde SES-document heeft geen totaalbedrag. Extracteer of corrigeer eerst het SES-document.');
                return;
            }
            if (this.isSelectedSesHoursMatch() === false) {
                const approved = this.getSelectedSesApprovedHours();
                const logged = this.getSelectedSesLoggedHours();
                this.toastr.error(`De gelogde uren (${logged}u) komen niet overeen met de SES-uren (${approved}u).`);
                return;
            }
        }

        const alreadyBilledSlots = this.selectedTimeslots.filter((slot) => slot.billed);
        if (alreadyBilledSlots.length > 0) {
            this.toastr.warning(`${alreadyBilledSlots.length} timeslot(s) have already been billed. Creating invoice anyway...`, 'Already Billed', {
                timeOut: 5000,
            });
        }

        this.subscriptions.add(
            this.resolveContactId().subscribe((contactId) => {
                if (!contactId) {
                    this.toastr.error('Geen passend Comanage contact gevonden. Controleer de leverancier/partner in het project.');
                    return;
                }

                const invoice = this.generateInvoiceJSON(contactId);
                this.createInvoiceInComanage(invoice);
            }),
        );
    }

    private resolveContactId(): Observable<number | null> {
        if (this.selectedComanageSupplierId) {
            return of(this.selectedComanageSupplierId);
        }

        const storedId = this.getStoredComanageSupplierId();
        if (storedId) {
            return of(storedId);
        }

        return this.comanageService.getContacts().pipe(
            map((contacts: any[]) => {
                if (!contacts || contacts.length === 0) {
                    return null;
                }

                return this.findContactIdFromProject(contacts);
            }),
            catchError((error) => {
                this.toastr.error('Kon Comanage contacten niet ophalen.');
                return of(null);
            }),
        );
    }

    private findContactIdFromProject(contacts: any[]): number | null {
        const normalize = (value: string | undefined | null) => value?.trim().toLowerCase();

        const candidateNames = [
            normalize(this.partnerName),
            normalize(this.project?.partner?.title),
            normalize(this.project?.companyName),
            normalize(this.project?.title),
        ].filter(Boolean) as string[];

        const supplierMatch = contacts.find((c) => {
            const contactName = normalize(c?.name);
            return c?.supplier && candidateNames.some((n) => contactName === n || contactName?.includes(n));
        });
        if (supplierMatch?.number) {
            return Number(supplierMatch.number);
        }

        const nameMatch = contacts.find((c) => {
            const contactName = normalize(c?.name);
            return candidateNames.some((n) => contactName === n || contactName?.includes(n));
        });
        if (nameMatch?.id) {
            return Number(nameMatch.id);
        }

        return null;
    }

    private getStoredComanageSupplierId(): number | null {
        const projectValue = this.parseSupplierId(this.project?.comanageContactId);
        if (projectValue) {
            return projectValue;
        }

        if (!this.project?.uuid) return null;
        try {
            const raw = localStorage.getItem('project-comanage-supplier-map');
            const map = raw ? (JSON.parse(raw) as Record<string, number>) : {};
            const value = map[this.project.uuid];
            return value ? Number(value) : null;
        } catch (e) {
            this.logger.warn('Failed to read supplier from local storage', e);
            return null;
        }
    }

    private saveSupplierForProject(supplierId: number | null): void {
        if (!this.project?.uuid || this.savingComanageSupplier) return;

        const nextComanageContactId = supplierId != null ? String(supplierId) : null;
        if ((this.project.comanageContactId || null) === nextComanageContactId) {
            return;
        }

        this.savingComanageSupplier = true;
        const payload: Project = {
            ...this.project,
            comanageContactId: nextComanageContactId,
        };

        this.subscriptions.add(
            this.projectService.updateProject(this.project.uuid, payload).subscribe({
                next: (updated) => {
                    this.project = updated;
                    this.selectedComanageSupplierId = this.parseSupplierId(updated?.comanageContactId);
                    this.savingComanageSupplier = false;
                },
                error: (err) => {
                    this.savingComanageSupplier = false;
                    this.logger.warn('Failed to persist Comanage customer on project', err);
                    this.toastr.error('Opslaan van project customer mislukt.');
                },
            }),
        );

        try {
            const raw = localStorage.getItem('project-comanage-supplier-map');
            const map = raw ? (JSON.parse(raw) as Record<string, number>) : {};
            if (supplierId != null) {
                map[this.project.uuid] = Number(supplierId);
            } else {
                delete map[this.project.uuid];
            }
            localStorage.setItem('project-comanage-supplier-map', JSON.stringify(map));
        } catch (err) {
            this.logger.warn('Failed to save supplier to local storage', err);
        }
    }

    private parseSupplierId(value: string | number | null | undefined): number | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }
        const parsed = Number(value);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    }

    createInvoiceInComanage(invoice: any): void {
        if (!this.comanageApiKey) {
            this.logger.error('InvoiceDialog: Comanage API key is not available');
            this.toastr.error('Comanage API key is not configured. Please configure it in settings.');
            this.subscriptions.unsubscribe();
            this.dialogRef.close(this.selectedTimeslots);
            return;
        }

        this.logger.debug('InvoiceDialog: Attempting to create invoice in Comanage (Step 1: Create invoice)');

        this.subscriptions.add(
            this.comanageService.createInvoiceInComanage(invoice, this.comanageApiKey).subscribe(
                (result) => {
                    this.logger.debug('InvoiceDialog: Invoice created successfully in Comanage');
                    console.log('CoManage Response:', result);

                    // Extract invoice ID/number from response - try multiple possible field names
                    let invoiceId = result?.invoice_number || result?.number || result?.id || result?.uuid || result?.invoice_id;

                    // If result is an array, try to get the first item
                    if (!invoiceId && Array.isArray(result) && result.length > 0) {
                        invoiceId = result[0]?.invoice_number || result[0]?.number || result[0]?.id || result[0]?.uuid;
                    }

                    // If result is an object with data property
                    if (!invoiceId && result?.data) {
                        invoiceId = result.data?.invoice_number || result.data?.number || result.data?.id || result.data?.uuid;
                    }

                    if (!invoiceId) {
                        this.logger.warn('InvoiceDialog: No invoice number/ID in response, skipping line items');
                        console.warn('Response structure:', JSON.stringify(result, null, 2));
                        this.toastr.success('Invoice created in Comanage successfully!');
                        this.subscriptions.unsubscribe();
                        this.dialogRef.close(this.selectedTimeslots);
                        return;
                    }

                    this.logger.debug('InvoiceDialog: Adding line items to invoice (Step 2)');
                    this.addInvoiceItems(invoiceId);
                },
                (error) => {
                    this.logger.error('InvoiceDialog: Failed to create invoice in Comanage:', error);
                    this.toastr.error('Failed to create invoice in Comanage. Check console for details.');
                    this.subscriptions.unsubscribe();
                    this.dialogRef.close(this.selectedTimeslots);
                },
            ),
        );
    }

    private addInvoiceItems(invoiceId: number | string): void {
        const items = this.generateInvoiceItems();

        console.log('🔍 [DEBUG] addInvoiceItems called with invoiceId:', invoiceId);
        console.log('🔍 [DEBUG] selectedTimeslots:', this.selectedTimeslots);

        if (items.length === 0) {
            this.logger.debug('InvoiceDialog: No items to add, applying invoice text then finalize');
            console.log('⚠️ No items, going to applyInvoiceTextThenFinalize');
            this.applyInvoiceTextThenFinalize(invoiceId);
            return;
        }

        this.subscriptions.add(
            this.comanageService.addInvoiceItems(invoiceId, items, this.comanageApiKey).subscribe(
                (result) => {
                    this.logger.debug('InvoiceDialog: Line items added successfully to invoice');
                    console.log('✅ Line items added, now applying invoice text then finalize');
                    this.applyInvoiceTextThenFinalize(invoiceId);
                },
                (error) => {
                    this.logger.warn('InvoiceDialog: Failed to add line items to invoice (invoice was created):', error);
                    console.log('⚠️ Line items failed, still applying invoice text then finalize');
                    this.applyInvoiceTextThenFinalize(invoiceId);
                },
            ),
        );
    }

    private uploadAttachments(invoiceId: number | string): void {
        // Uploading attachments disabled per request. Only reference them in invoice extra text.
        this.logger.debug('InvoiceDialog: Skipping attachments upload; applying invoice text then finalize');
        this.applyInvoiceTextThenFinalize(invoiceId);
    }

    private finalizeInvoice(invoiceId: number | string): void {
        this.logger.debug('InvoiceDialog: Finalizing invoice (Step 3)');

        this.subscriptions.add(
            this.comanageService.finalizeInvoiceInComanage(invoiceId, this.comanageApiKey).subscribe(
                (result) => {
                    this.logger.debug('InvoiceDialog: Invoice finalized successfully');
                    this.toastr.success('Invoice created and finalized in Comanage successfully!');
                    // Save finalized invoice metadata so we can show PDF link in the dialog
                    try {
                        this.finalizedInvoice = result && result.data ? result.data : null;
                        this.finalizedInvoicePdfUrl = this.finalizedInvoice?.pdf || null;
                        this.finalizedInvoiceLink = this.buildComanageEditLink(this.finalizedInvoice);
                        this.finalizedInvoiceNumber = this.finalizedInvoice?.invoice_number || this.finalizedInvoice?.number || String(invoiceId);
                        this.finalizedInvoiceId = this.getComanageInvoiceId(this.finalizedInvoice) || String(invoiceId);
                        this.finalizedInvoiceStatus = this.getComanageInvoiceStatus(this.finalizedInvoice) || null;
                        this.finalizedInvoiceDate = this.getComanageInvoiceDate(this.finalizedInvoice) || null;
                    } catch {
                        /* noop */
                    }

                    // Immediately persist invoice references to backend + NGXS for each selected timeslot
                    this.persistInvoiceReferencesForSelectedTimeslots();

                    // Notify parent/listeners that an invoice was created (so lists can refresh)
                    try {
                        window.dispatchEvent(new CustomEvent('invoiceCreated', { detail: this.finalizedInvoice }));
                    } catch {}

                    // Keep dialog open so user can see PDF icon in the popup; do not auto-close
                },
                (error) => {
                    this.logger.error('InvoiceDialog: Failed to finalize invoice:', error);
                    this.toastr.warning('Invoice created with items, but finalization failed.');
                    // keep dialog open to allow retry or inspection
                },
            ),
        );
    }

    /**
     * Persist the finalized invoice references (pdf, link, invoice_number) on every
     * selected timeslot — both in the backend and in the NGXS store — so the data
     * survives even if the user closes the dialog without clicking "Sluiten".
     */
    private persistInvoiceReferencesForSelectedTimeslots(): void {
        const pdfUrl = this.finalizedInvoicePdfUrl || undefined;
        const invoiceNumber = this.finalizedInvoiceNumber ? String(this.finalizedInvoiceNumber) : undefined;
        const invoiceId = this.finalizedInvoiceId || undefined;
        const invoiceStatus = this.finalizedInvoiceStatus || undefined;
        const invoiceDate = this.finalizedInvoiceDate || undefined;
        const comanageLink = this.finalizedInvoiceLink || undefined;

        const selectedSesAmount = this.invoiceMode === 'ses' ? this.getSelectedSesAmount() : 0;
        const selectedSesMinutes =
            this.invoiceMode === 'ses' ? (this.selectedTimeslots || []).reduce((sum, slot) => sum + this.getSesAllocatedMinutesForTimeslot(slot), 0) : 0;
        const selectedSesNumber = this.invoiceMode === 'ses' ? (this.sesNumber || this.getSesDocumentReference(this.selectedSesDoc) || '').trim() : '';
        const selectedSesDocumentId = this.invoiceMode === 'ses' ? this.selectedSesDoc?.fileId || this.selectedSesFileId || '' : '';
        const selectedPoNumber = (this.poNumber || this.selectedPoDoc?.extractedPoNumber || '').trim();
        const selectedPoDocumentId = this.selectedPoDoc?.fileId || this.selectedPoFileId || '';

        (this.selectedTimeslots || []).forEach((slot) => {
            // Resolve parent timesheet UUID
            let timesheetUUID = slot.timesheet?.uuid;
            if (!timesheetUUID) {
                const parent = (this.timesheets || []).find((ts) => (ts.timeslots || []).some((s) => s.uuid === slot.uuid));
                timesheetUUID = parent?.uuid;
            }
            if (this.invoiceMode === 'ses') {
                // SES invoicing must use allocations as the reusable source of truth.
                // A project/timeslot can be spread over multiple SES documents, so do
                // not blindly mark the whole timeslot as billed unless the allocation
                // covers the remaining minutes.
                const allocatedMinutes = this.getSesAllocatedMinutesForTimeslot(slot);
                if (allocatedMinutes <= 0) {
                    return;
                }
                const amountExcludingVat =
                    selectedSesMinutes > 0 ? Number((selectedSesAmount * (allocatedMinutes / selectedSesMinutes)).toFixed(2)) : undefined;
                this.timesheetsService
                    .addTimeslotInvoiceAllocation(
                        slot.uuid,
                        allocatedMinutes,
                        amountExcludingVat,
                        'PROJECT',
                        selectedSesDocumentId,
                        selectedSesNumber,
                        selectedPoDocumentId,
                        selectedPoNumber,
                        invoiceNumber,
                        invoiceId,
                        invoiceStatus,
                        invoiceDate,
                        pdfUrl,
                        comanageLink,
                    )
                    .subscribe({
                        error: (err) => this.logger.warn('Failed to persist invoice allocation for timeslot', slot.uuid, err),
                    });
                slot.invoiceAllocations = [
                    ...(slot.invoiceAllocations || []),
                    {
                        invoiceNumber,
                        invoiceId,
                        invoiceStatus,
                        invoiceDate,
                        invoicePdfUrl: pdfUrl,
                        invoiceLink: comanageLink,
                        sesDocumentId: selectedSesDocumentId,
                        sesNumber: selectedSesNumber,
                        poDocumentId: selectedPoDocumentId,
                        poNumber: selectedPoNumber,
                        allocatedMinutes,
                        amountExcludingVat,
                        rateType: 'PROJECT',
                    },
                ];

                const alreadyAllocated = this.getAllocatedMinutesForTimeslot(slot);
                const shouldMarkBilled = alreadyAllocated >= (slot?.minutes || 0) - 0.5;
                if (shouldMarkBilled) {
                    this.store.dispatch(
                        new SetTimeslotToIsBilled(timesheetUUID, slot.uuid, true, invoiceNumber, invoiceId, invoiceStatus, invoiceDate, pdfUrl, comanageLink),
                    );
                }
            } else {
                // Non-SES invoice modes still bill the selected timeslot as one unit.
                this.timesheetsService.setTimeslotToIsBilled(slot.uuid, invoiceNumber, invoiceId, invoiceStatus, invoiceDate, pdfUrl, comanageLink).subscribe({
                    error: (err) => this.logger.warn('Failed to persist invoice ref for timeslot', slot.uuid, err),
                });
                this.store.dispatch(
                    new SetTimeslotToIsBilled(timesheetUUID, slot.uuid, true, invoiceNumber, invoiceId, invoiceStatus, invoiceDate, pdfUrl, comanageLink),
                );
            }
        });
        this.finalizedInvoiceResults.push({
            timeslots: this.selectedTimeslots,
            sesDocumentId: selectedSesDocumentId || undefined,
            sesNumber: selectedSesNumber || undefined,
            poDocumentId: selectedPoDocumentId || undefined,
            poNumber: selectedPoNumber || undefined,
            pdfUrl,
            invoiceNumber,
            invoiceId,
            invoiceStatus,
            invoiceDate,
            comanageLink,
        });
    }

    private getAllocatedMinutesForTimeslot(slot: Timeslot): number {
        return (slot?.invoiceAllocations || [])
            .map((allocation) => Number(allocation?.allocatedMinutes || 0))
            .filter((value) => Number.isFinite(value))
            .reduce((sum, value) => sum + value, 0);
    }

    private composeExtraTextWithAttachments(): string {
        const po = (this.poNumber || '').trim();
        const ses = (this.sesNumber || this.getSesDocumentReference(this.selectedSesDoc) || '').trim();
        const sesDocumentUrl = this.getSelectedSesDocumentUrl();
        const jiraKey = (this.jiraReferenceDisplay || '').trim();
        const baseNote = (this.comanageInvoiceText || '').trim();
        // Only include explicitly selected timesheet attachments for 'time' mode
        const selectedAttachmentMap = new Map<string, { timeslotUuid: string; pdfUrl: string; fileName: string }>();
        (this.selectedAttachments || []).forEach((attachment) => {
            if (attachment?.timeslotUuid && attachment?.pdfUrl) {
                selectedAttachmentMap.set(attachment.timeslotUuid, attachment);
            }
        });

        const slotsWithSelectedPdf =
            this.invoiceMode === 'time' ? (this.selectedTimeslots || []).filter((timeslot) => !!selectedAttachmentMap.get(timeslot?.uuid)) : [];
        const lines: string[] = [];

        // Always include Our reference when available (bold label)
        if (jiraKey.length > 0) {
            lines.push(`<strong>Our reference:</strong> ${jiraKey}`);
        }
        // Remove explicit Tempo account line; account key is appended to Our reference
        if (po.length > 0) {
            lines.push(`<strong>PO-nr:</strong> ${po}`);
        }
        if (ses.length > 0) {
            lines.push(`<strong>SES-nr:</strong> ${ses}`);
        }
        if (baseNote.length > 0) {
            lines.push('');
            lines.push(baseNote);
        }

        // Attachments references
        if (sesDocumentUrl.length > 0 || (this.invoiceMode === 'time' && slotsWithSelectedPdf.length > 0)) {
            lines.push('');
            lines.push('<strong>Attachments:</strong>');
        }
        if (sesDocumentUrl.length > 0) {
            lines.push(`- SES-document${ses ? ` ${ses}` : ''}:`);
            lines.push(this.encodeUrl(sesDocumentUrl));
        }
        if (this.invoiceMode === 'time') {
            slotsWithSelectedPdf.forEach((ts) => {
                const attachment = selectedAttachmentMap.get(ts.uuid);
                if (!attachment?.pdfUrl) {
                    return;
                }
                const emp = this.getEmployeeByTimeslot(ts);
                const empName = emp ? `${emp.firstName} ${emp.lastName}` : 'Unknown';
                const monthLabel = this.getMonthLabelForTimeslot(ts);
                lines.push(`- Timesheet ${empName}${monthLabel ? ` (${monthLabel})` : ''}:`);
                lines.push(this.encodeUrl(attachment.pdfUrl));
            });
        }
        return lines.join('\n');
    }

    private getSelectedSesDocumentUrl(): string {
        const doc = this.selectedSesDoc;
        if (!doc) {
            return '';
        }

        const candidates = [
            doc.title,
            (doc as any).url,
            this.firstText(null, doc.extractedData || {}, ['url', 'fileUrl', 'file_url', 'documentUrl', 'document_url', 'downloadUrl', 'download_url']),
        ];

        for (const candidate of candidates) {
            const value = (candidate || '').toString().trim();
            if (/^https?:\/\//i.test(value)) {
                return value;
            }
        }

        return '';
    }

    private encodeUrl(url: string): string {
        try {
            // Ensure spaces and other unsafe characters are encoded but keep URL structure intact
            return encodeURI(url);
        } catch {
            return url;
        }
    }

    private getMonthLabelForTimeslot(ts: Timeslot): string {
        try {
            // Prefer parsing from the PDF file name: e.g. "2026-01 Reinout Krekels.pdf"
            const fileName = this.extractFileName(ts.pdfUrl || '');
            const m = fileName.match(/(\d{4})-(\d{2})/);
            if (m) {
                const year = Number(m[1]);
                const month = Number(m[2]);
                const date = moment(`${year}-${String(month).padStart(2, '0')}-01`).locale('en');
                if (date.isValid()) {
                    return date.format('MMMM').toLowerCase(); // e.g., "january"
                }
            }
            // Fallback: optional label from previous months grouping
            const label = (ts as any)._previousMonthLabel as string | undefined;
            return label ? label.toLowerCase() : '';
        } catch {
            return '';
        }
    }

    private applyInvoiceTextThenFinalize(invoiceId: number | string): void {
        const extraText = this.composeExtraTextWithAttachments();
        if (!extraText) {
            this.finalizeInvoice(invoiceId);
            return;
        }
        this.logger.debug('InvoiceDialog: Applying extra text (with attachments references) to invoice');
        this.subscriptions.add(
            this.comanageService.setInvoiceText(invoiceId, extraText, this.comanageApiKey!).subscribe(
                () => {
                    this.logger.debug('InvoiceDialog: Invoice text applied successfully');
                    this.finalizeInvoice(invoiceId);
                },
                (error) => {
                    this.logger.warn('InvoiceDialog: Failed to apply invoice text, continuing to finalize', error);
                    this.finalizeInvoice(invoiceId);
                },
            ),
        );
    }

    private loadInvoiceFieldsFromContracts(): void {
        if (!this.project) {
            this.poNumber = '';
            this.sesNumber = '';
            this.selectedPoFileId = '';
            this.selectedSesFileId = '';
            this.comanageInvoiceText = '';
            return;
        }

        const seedTimeslot =
            this.selectedTimeslots && this.selectedTimeslots.length > 0
                ? this.selectedTimeslots[0]
                : this.timeslots && this.timeslots.length > 0
                ? this.timeslots[0]
                : null;
        const bestLink = seedTimeslot ? this.getBestDefaultLinkForTimeslot(seedTimeslot) : null;

        this.poNumber = (bestLink?.poNumber || '').toString();
        this.sesNumber = (bestLink?.sesNumber || '').toString();

        const projectText = (this.project?.contractFreeText || '').toString().trim();
        const employeeText = (bestLink?.freeText || '').toString().trim();
        this.comanageInvoiceText = [projectText, employeeText]
            .filter((v) => v.length > 0)
            .join('\n')
            .trim();
        this.updateCreateDisabled();
    }

    private refreshInvoiceFieldsByMode(): void {
        if (this.invoiceMode === 'account') {
            this.loadInvoiceFieldsFromSelectedAccounts();
            return;
        }
        if (this.invoiceMode === 'ses') {
            if (this.selectedSesDoc) {
                this.applySelectedSesData(this.selectedSesDoc);
                this.applySelectedSesToTimeslots();
            }
            this.updateCreateDisabled();
            return;
        }
        this.loadInvoiceFieldsFromContracts();
    }

    private loadInvoiceFieldsFromSelectedAccounts(): void {
        if (!this.project) {
            this.poNumber = '';
            this.sesNumber = '';
            this.selectedPoFileId = '';
            this.selectedSesFileId = '';
            this.comanageInvoiceText = '';
            return;
        }

        const { from, to } = this.getWorklogRange();
        const selectedAccounts = this.getSelectedTempoAccounts();
        if (!selectedAccounts.length) {
            this.loadInvoiceFieldsFromContracts();
            return;
        }

        const projectText = (this.project?.contractFreeText || '').toString().trim();
        const resolvedAccounts = selectedAccounts.map((selectedAccount) => ({
            selectedAccount,
            matchedProjectAccount: this.findProjectAccountForPeriod(selectedAccount, from, to),
        }));

        const poValues = resolvedAccounts
            .map(({ selectedAccount, matchedProjectAccount }) => {
                const po = (matchedProjectAccount?.poNumber || '').toString().trim();
                if (!po) return '';
                const label = (selectedAccount?.key || selectedAccount?.id || '').toString().trim();
                return label ? `[${label}] ${po}` : po;
            })
            .filter((value: string) => value.length > 0);

        const sesValues = resolvedAccounts
            .map(({ selectedAccount, matchedProjectAccount }) => {
                const ses = (matchedProjectAccount?.sesNumber || '').toString().trim();
                if (!ses) return '';
                const label = (selectedAccount?.key || selectedAccount?.id || '').toString().trim();
                return label ? `[${label}] ${ses}` : ses;
            })
            .filter((value: string) => value.length > 0);

        const freeTextEntries = resolvedAccounts
            .map(({ matchedProjectAccount }) => {
                const freeText = (matchedProjectAccount?.freeText || '').toString().trim();
                if (!freeText) return '';
                const accountLabel = (matchedProjectAccount?.accountName || matchedProjectAccount?.jiraAccountId || '').toString().trim();
                return accountLabel ? `[${accountLabel}] ${freeText}` : freeText;
            })
            .filter((value: string) => value.length > 0);

        this.poNumber = poValues.join(' | ');
        this.sesNumber = sesValues.join(' | ');
        this.comanageInvoiceText = [projectText, ...freeTextEntries]
            .filter((value) => value.length > 0)
            .join('\n')
            .trim();
        this.updateCreateDisabled();
    }

    private findProjectAccountForPeriod(account: { id: string; key?: string; name?: string }, fromIso: string, toIso: string): any | null {
        try {
            const accountId = (account?.id || '').toString().trim();
            const accountKey = (account?.key || '').toString().trim();
            const accountName = (account?.name || '').toString().trim();

            if (!this.projectAccounts || !this.projectAccounts.length) return null;

            const from = moment(fromIso, 'YYYY-MM-DD');
            const to = moment(toIso, 'YYYY-MM-DD');

            const normalizedAccountId = accountId.toLowerCase();
            const normalizedAccountKey = accountKey.toLowerCase();
            const normalizedAccountName = accountName.toLowerCase();

            const candidates = (this.projectAccounts || []).filter((projectAccount: any) => {
                const projectAccountId = String(projectAccount?.jiraAccountId || projectAccount?.accountId || '').trim();
                const projectAccountCode = String(projectAccount?.accountCode || projectAccount?.accountKey || projectAccount?.key || '').trim();
                const projectAccountName = String(projectAccount?.accountName || '').trim();

                const normalizedProjectAccountId = projectAccountId.toLowerCase();
                const normalizedProjectAccountCode = projectAccountCode.toLowerCase();
                const normalizedProjectAccountName = projectAccountName.toLowerCase();

                const idMatch =
                    !!normalizedProjectAccountId && (normalizedProjectAccountId === normalizedAccountId || normalizedProjectAccountId === normalizedAccountKey);

                const codeMatch =
                    !!normalizedProjectAccountCode &&
                    (normalizedProjectAccountCode === normalizedAccountKey ||
                        normalizedProjectAccountCode === normalizedAccountId ||
                        (!!normalizedAccountKey && normalizedProjectAccountCode.includes(normalizedAccountKey)));

                const nameMatch =
                    !!normalizedProjectAccountName &&
                    (normalizedProjectAccountName === normalizedAccountName ||
                        normalizedProjectAccountName === normalizedAccountKey ||
                        (!!normalizedAccountKey && normalizedProjectAccountName.includes(normalizedAccountKey)) ||
                        (!!normalizedAccountName && normalizedProjectAccountName.includes(normalizedAccountName)));

                return idMatch || codeMatch || nameMatch;
            });

            if (!candidates.length) return null;

            const applicable = candidates.filter((candidate: any) => {
                const effectiveFrom = candidate.effectiveFrom ? moment(candidate.effectiveFrom, 'YYYY-MM-DD') : null;
                const effectiveTo = candidate.effectiveTo ? moment(candidate.effectiveTo, 'YYYY-MM-DD') : null;
                const fromOk = !effectiveFrom || effectiveFrom.isSameOrBefore(to);
                const toOk = !effectiveTo || effectiveTo.isSameOrAfter(from);
                return fromOk && toOk;
            });

            const source = applicable.length ? applicable : candidates;
            const sorted = source.sort((left: any, right: any) => {
                const leftFrom = left.effectiveFrom ? moment(left.effectiveFrom, 'YYYY-MM-DD').valueOf() : -Infinity;
                const rightFrom = right.effectiveFrom ? moment(right.effectiveFrom, 'YYYY-MM-DD').valueOf() : -Infinity;
                return rightFrom - leftFrom;
            });

            return sorted[0] || null;
        } catch {
            return null;
        }
    }

    onPoNumberInput(value: string): void {
        this.poNumber = value || '';
        this.updateCreateDisabled();
    }

    onSesNumberInput(value: string): void {
        this.sesNumber = value || '';
        this.updateCreateDisabled();
    }

    onComanageInvoiceTextInput(value: string): void {
        this.comanageInvoiceText = value || '';
    }

    onPoNumberBlur(): void {
        this.poNumber = (this.poNumber || '').trim();
        this.updateCreateDisabled();
    }

    onSesNumberBlur(): void {
        this.sesNumber = (this.sesNumber || '').trim();
        this.updateCreateDisabled();
    }

    onComanageInvoiceTextBlur(): void {
        this.comanageInvoiceText = (this.comanageInvoiceText || '').trim();
    }
}
